/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Modal, Select, styled, SxProps } from "@mui/material";
import { keyframes } from "@mui/system";
import { Field, Form } from "react-final-form";
import ContentUploadNew from "./components/ContentUploadNew";

import { AxiosResponse } from "axios";
import { id as keccakId } from "ethers";
import { createForm, FormApi } from "final-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { fromWei, toWei } from "web3-utils";
import CheckBoxFieldComponent from "../../components/FormComponents/CheckBoxField.component";
import SelectFieldControl from "../../components/FormComponents/SelectField.component";
import TextFieldControlSimple from "../../components/FormComponents/TextField.component";
import MapDialog from "../../components/MapDialog";
import { ActionBtn, Text } from "../../components/Typography";
import { AppRoutes } from "../../config";
import ApiRoutes from "../../config/api.routes";
import { MAPBOX_TOKEN } from "../../config/mapbox";
import useMetaMask from "../../contexts/useMetaMask";
import {
  ecoFuturesAdminContractAddress,
  getContract,
  getMetaMaskSigner,
  getReadOnlyContract,
} from "../../services/contract.service";
import { useAxios } from "../../services/http.service";
import { COUNTRY_CODES, CURRENCY_NAME, TERM_UNIT } from "../../utils/constants";
import FormValidators from "../../utils/form-validators";
import {
  checkTransactionConfirmationReceipt,
  consoleError,
  consoleLog,
  getCountriesList,
  sizing,
} from "../../utils/methods";
import { Attention } from "../BecomeTrust";
import MultiTextInput from "./components/MultiTextInput";
import SelectItemsInput from "./components/SelectItemsInput";
import { Zone } from "../../types/LandToken";
// import { INITIAL_SCAN } from "../../utils/constants";
import ProgressStepper from "../../components/ProgressStepper";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { duration } from "moment";
import { BlogStoryBlock } from "../../components/FAQBlock";
import InfoCard from "../../components/InfoCard/InfoCard";
import useAuthorization from "../../contexts/useAuthorization";
import blogs from "../About/blogs";
import { Trending } from "../Home/Home";
import { initializeBlogContent } from "../SupportCenter/BlogArticle";
import DocUploadNew from "./components/DocUploadNew";
import CheckIcon from "@mui/icons-material/Check";
import InfoIconOutlined from "@mui/icons-material/InfoOutlined";
import { useProfileDataCache } from "../../contexts";

const overlaySlideIn = keyframes`
  from { transform: translate3d(-32px, 0, 0); opacity: 0; }
  to   { transform: translate3d(0, 0, 0);     opacity: 1; }
`;

// Land-upload success animations (ported from the v6 mockup).
// Conic-gradient "disc glow" that spins under the status icon while uploading.
const landUploadDiscSpin = keyframes`
  to { transform: rotate(360deg); }
`;

// One-shot purple bloom that bursts outward from the icon when the upload
// completes. Compositor-only (transform/opacity); fires once via `both`.
const landUploadRippleOut = keyframes`
  0%   { transform: translate3d(-50%, -50%, 0) scale(0.55); opacity: 0; }
  18%  { transform: translate3d(-50%, -50%, 0) scale(0.85); opacity: 1; }
  100% { transform: translate3d(-50%, -50%, 0) scale(3.6);  opacity: 0; }
`;

// Thin definition ring riding just behind the bloom.
const landUploadRingOut = keyframes`
  0%   { transform: translate3d(-50%, -50%, 0) scale(0.6);  opacity: 0; }
  18%  { transform: translate3d(-50%, -50%, 0) scale(0.95); opacity: 0.8; }
  100% { transform: translate3d(-50%, -50%, 0) scale(3.8);  opacity: 0; }
`;

// Monospace font for numeric input fields (lat/lon, size, elevation, bids, contract period).
const monoFieldSx: SxProps = {
  "& input": { fontFamily: "monospace" },
};

// Pre-fills location.country from the phone country code chosen in step 1,
// but only when the user hasn't picked a country yet. Doesn't overwrite a
// country that's already been set (manually or by reverse geocoding).
const CountryPrefiller = ({
  phoneCode,
  currentCountry,
  codes,
  form,
}: {
  phoneCode?: string;
  currentCountry?: string;
  codes: any[];
  form: any;
}) => {
  useEffect(() => {
    if (currentCountry) return;
    if (!phoneCode || !codes?.length) return;
    const match = codes.find((c: any) => c.value === phoneCode);
    const countryName = match?.label?.split(" | ")[0]?.trim();
    if (countryName) {
      form.mutators.setLocation({ country: countryName });
    }
  }, [phoneCode, currentCountry, codes, form]);
  return null;
};

// Reverse-geocodes location.lat/lon via Mapbox (debounced) and writes the
// resolved place + country back into the form. Runs for both map clicks and
// manually typed coordinates.
const LocationGeocoder = ({ lat, lon, form }: { lat?: any; lon?: any; form: any }) => {
  useEffect(() => {
    const numLat = Number(lat);
    const numLon = Number(lon);
    if (!Number.isFinite(numLat) || !Number.isFinite(numLon)) return;
    if (numLat === 0 && numLon === 0) return;
    if (numLat < -90 || numLat > 90 || numLon < -180 || numLon > 180) return;

    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${numLon},${numLat}.json?access_token=${MAPBOX_TOKEN}`
        );
        const data = await response.json();
        if (data?.features?.length > 0) {
          const placeFeature =
            data.features.find((f: any) => f.place_type?.includes("place")) ||
            data.features.find((f: any) => f.place_type?.includes("locality")) ||
            data.features.find((f: any) => f.place_type?.includes("region")) ||
            data.features.find((f: any) => f.place_type?.includes("district")) ||
            data.features[0];
          const countryFeature = data.features.find((f: any) => f.place_type?.includes("country"));
          form.mutators.setLocation({
            name: placeFeature?.text || "",
            country: countryFeature?.text || "",
          });
        }
      } catch (e) {
        console.error("Reverse geocoding failed:", e);
      }
    }, 600);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon]);

  return null;
};

const LocationScrollTrigger = ({
  lat,
  lon,
  targetRef,
}: {
  lat?: any;
  lon?: any;
  targetRef: React.RefObject<HTMLDivElement>;
}) => {
  useEffect(() => {
    if (!lat || !lon) return;
    if (Number(lat) === 0 && Number(lon) === 0) return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    const el = targetRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(timer);
  }, [lat, lon, targetRef]);
  return null;
};

const CharCount = ({ value, max }: { value?: string; max: number }) => {
  const len = typeof value === "string" ? value.length : 0;
  return (
    <Box
      sx={{
        textAlign: "right",
        fontSize: "12px",
        color: len > max ? "error.main" : "#818181",
        mt: "4px",
        fontFamily: "monospace",
      }}
    >
      {len}/{max}
    </Box>
  );
};

export type Unit = "hectares" | "acres" | "sqm" | "sqft" | "parcel";

export function convertToParcels(size: number, unit: Unit) {
  // convert input size to square meters
  let sizeSqMeters = 0;
  switch (unit) {
    case "sqm":
      sizeSqMeters = size;
      break;
    case "acres":
      sizeSqMeters = size * 4046.86;
      break;
    case "hectares":
      sizeSqMeters = size * 10000;
      break;
    case "sqft":
      sizeSqMeters = size / 10.764;
      break;
    case "parcel":
      return size;
    default:
      console.log("Invalid unit type");
      return 0;
  }

  // calculate number of parcels
  return Math.floor(sizeSqMeters / 100);
}

type ScanValues = {
  tokenId: string | null;
  deedCheck: boolean;
  contentAlias?: string | null;
  content?: File | null;
  survey?: File | null;
  surveyPlan?: File | null;
  deed?: File | null;
  name: string;
  description: string;
  size: any;
  zones?: Zone[];
  parcels: number;
  elevationMin: number;
  elevationMax: number;
  location: {
    name: string;
    lat: string;
    lon: string;
    country: string;
  };
  biomes: any;
  flora: any;
  fauna: any;
  dwellings: any;
  production: any;
  bfd_plan: string;
  basicPerks: any;
  contract_period: number | undefined;
  startingBid: string;
  easements?: { description: string; perks: string; activationBid: number }[];
  additional_contacts: string[];
  phoneCode: string;
  files: any[];
};

export const initialValues: ScanValues = {
  tokenId: null,
  deedCheck: false,
  name: "",
  description: "",
  size: undefined,
  parcels: 0,
  elevationMin: 0,
  elevationMax: 0,
  location: {
    name: "",
    lat: "",
    lon: "",
    country: "",
  },
  biomes: "",
  flora: "",
  fauna: "",
  dwellings: "",
  production: "",
  bfd_plan: "",
  basicPerks: "",
  contract_period: undefined,
  startingBid: "",
  additional_contacts: [],
  phoneCode: "",
  easements: [],
  files: [],
};

const Scan = () => {
  const axios = useAxios();
  const { library } = useMetaMask();
  const navigator = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeStep, setStep] = useState(-1);

  type TransactionStatus = "idle" | "processing" | "success" | "error";
  const [approveStatus, setApproveStatus] = useState<TransactionStatus>("idle");
  const [, setConfirmStatus] = useState<TransactionStatus>("idle");

  const [error, setError] = useState<string>("");
  const [imagePreview, setPreview] = useState<any>(null);
  const [isLoading, setLoading] = useState(false);

  const [easementCount, setEasementCount] = useState(0);

  // docs section
  const [contentFile, setContentRaw] = useState<File | undefined>(undefined);
  const setContent = (file: File | undefined) => {
    uploadedContentAliasRef.current = null;
    setContentRaw(file);
  };
  const [surveyFile, setSurvey] = useState<File | undefined>(undefined);
  const [deedFile, setDeedFile] = useState<File | undefined>(undefined);

  // land size
  const [sizeUnit, setSizeUnit] = useState<Unit>("hectares");
  const [sizeValue, setSizeValue] = useState<number | string>("");
  const [sizeParcels, setSizeParcels] = useState(0);

  const [sizeMin, setSizeMin] = useState(1);
  const [sizeMax, setSizeMax] = useState(3333);

  // zones
  // const [zones, setZones] = useState<Zone[]>([]);
  // const [zoneName, setZoneName] = useState("");
  // const [zoneSize, setZoneSize] = useState("");

  const [contractMin, setContractMin] = useState(3);
  const [contractMax, setContractMax] = useState(99);
  const [initialFlora, setInitialFlora] = useState<{ key: number; label: string }[]>([]);
  const [initialDwellings, setInitialDwellings] = useState<{ key: number; label: string }[]>([]);
  const [initialProduction, setInitialProduction] = useState<{ key: number; label: string }[]>([]);
  const [initialPerks, setInitialPerks] = useState<{ key: number; label: string }[]>([]);
  const [initialPerks1, setInitialPerks1] = useState<{ key: number; label: string }[]>([]);
  const [initialPerks2, setInitialPerks2] = useState<{ key: number; label: string }[]>([]);
  const [countriesList, setCountriesList] = useState([]);
  const [availableCountries, setAvailableCountries] = useState<any[]>([]);
  const [trusteeInCountry, setTrusteeInCountry] = useState(false);

  // const theme = useTheme();
  // const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuthorization();
  const { refreshProfileData } = useProfileDataCache();
  const steps = ["Land ownership", "Land location", "Land description", "Land easement"];
  // const [open, setOpen] = useState(true);
  const [open1, setOpen1] = useState(false);
  const [landUploadSuccess, setLandUploadSuccess] = useState(false);

  // Tracked so the success-modal ripple is suppressed for reduced-motion users.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!open1) {
      setLandUploadSuccess(false);
      return;
    }
    const t = setTimeout(() => setLandUploadSuccess(true), 2000);
    return () => clearTimeout(t);
  }, [open1]);

  // const handleClose = () => setOpen(false);

  const handleClose1 = () => {
    setOpen1(false);
    navigator(`${AppRoutes.Profile.subRoutes.Collection.path}?activeTab=lands`);
  };
  const [blogArticles, setBlogArticles] = useState<{ [key: string]: any }>({});
  const [blogLoading, setBlogLoading] = useState(true);

  const [tokenId, setTokenId] = useState<string | null>(null);
  const [newDraft, setNewDraft] = useState(false);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [verificationFee, setVerificationFee] = useState<string | null>(null);
  // The three components behind verificationFee, for the breakdown under the total.
  const [verificationFeeParts, setVerificationFeeParts] = useState<{
    scaled: string;
    attestation: string;
    judgment: string;
  } | null>(null);
  const [verificationFeePerHectare, setVerificationFeePerHectare] = useState<string | null>(null);
  const [isDraftLoading, setIsDraftLoading] = useState(true);
  const [isRejected, setIsRejected] = useState(false);

  // Track uploaded files to prevent duplicates
  const [uploadedFiles, setUploadedFiles] = useState<{
    surveyPlan?: { name: string; size: number };
    deed?: { name: string; size: number };
  }>({});

  // Track last saved form values to prevent redundant API calls
  const lastSavedValuesRef = useRef<any>(null);
  // Stores the contentAlias returned after the first S3 PUT so subsequent
  // "Next" clicks reuse it instead of re-uploading the same file.
  const uploadedContentAliasRef = useRef<string | null>(null);
  const locationOverlayRef = useRef<HTMLDivElement>(null);

  // Helper function to compare form values and detect changes
  const hasFormDataChanged = (currentValues: any, lastSavedValues: any): boolean => {
    if (!lastSavedValues) return true; // First save, always save

    // Key fields to compare for changes
    const fieldsToCompare = [
      "name",
      "description",
      "size",
      "elevationMin",
      "elevationMax",
      "biomes",
      "flora",
      "dwellings",
      "production",
      "bfd_plan",
      "basicPerks",
      "contract_period",
      "startingBid",
      "deedCheck",
    ];

    for (const field of fieldsToCompare) {
      if (JSON.stringify(currentValues[field]) !== JSON.stringify(lastSavedValues[field])) {
        console.log(`[DIRTY CHECK] Field '${field}' changed:`, lastSavedValues[field], "->", currentValues[field]);
        return true;
      }
    }

    // Compare location object
    if (JSON.stringify(currentValues.location) !== JSON.stringify(lastSavedValues.location)) {
      console.log("[DIRTY CHECK] Location changed");
      return true;
    }

    // Compare additional_contacts array
    if (JSON.stringify(currentValues.additional_contacts) !== JSON.stringify(lastSavedValues.additional_contacts)) {
      console.log("[DIRTY CHECK] additional_contacts changed");
      return true;
    }

    // Compare phoneCode
    if (currentValues.phoneCode !== lastSavedValues.phoneCode) {
      console.log("[DIRTY CHECK] phoneCode changed");
      return true;
    }

    // Compare easements
    if (JSON.stringify(currentValues.easements) !== JSON.stringify(lastSavedValues.easements)) {
      console.log("[DIRTY CHECK] easements changed");
      return true;
    }

    console.log("[DIRTY CHECK] No changes detected, skipping save");
    return false;
  };

  const loadBlogContent = async () => {
    try {
      setBlogLoading(true);
      const content = await initializeBlogContent();
      setBlogArticles(content);
    } catch (error) {
      console.error("Failed to load blog content:", error);
    } finally {
      setBlogLoading(false);
    }
  };

  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    consoleLog(imagePreview);
  }, [activeStep]);

  const convertToDMS = (lat: number, lon: number): string => {
    if (!lat || !lon) {
      return "";
    }

    const convertCoordinate = (decimal: number, isLatitude: boolean) => {
      const degrees = Math.floor(Math.abs(decimal));
      const minutes = Math.floor((Math.abs(decimal) - degrees) * 60);
      const seconds = Math.round(((Math.abs(decimal) - degrees) * 60 - minutes) * 60);

      const direction = isLatitude ? (decimal >= 0 ? "N" : "S") : decimal >= 0 ? "E" : "W";

      return `${degrees}°${minutes}'${seconds}"${direction}`;
    };

    const latDMS = convertCoordinate(lat, true);
    const lonDMS = convertCoordinate(lon, false);

    return `${latDMS}, ${lonDMS}`;
  };

  const handleMapChoose = (formAPI: FormApi<ScanValues>) => (latLon: any) => {
    formAPI.mutators.setLocation({
      lat: latLon?.lat,
      lon: latLon?.lon,
      name: "Loading ...",
    });
  };

  // The country the guardian picked on the map, as the contract's ISO numeric code. 0 when the
  // form has no country yet — the contract then quotes at the global per-hectare rate, which is
  // also what it charges for country code 0, so the preview and the pull agree either way.
  const resolveCountryCode = (values: any): number => {
    const locationRaw = values?.location;
    const locationObj = typeof locationRaw === "string" ? JSON.parse(locationRaw) : locationRaw;
    const countryName: string = locationObj?.country ?? "";
    return COUNTRY_CODES.find((c) => c.name === countryName)?.countryCode ?? 0;
  };

  // V10.1: the upfront fee is THREE components — the area-scaled verification fee (deed check +
  // field survey + determination, from Admin.verificationFeeFor) plus the country's flat
  // attestation and judgment fees — and requestVerification pulls the whole thing in ONE
  // transfer. EcoLens.verificationTotalFor(landSize, countryCode) returns exactly that total,
  // with its parts, from the same formula the pull uses. Admin.calculateVerificationFee is gone.
  // landSize is in parcels (hundredths of a hectare), as everywhere else on this page.
  const quoteVerificationFee = async (landSizeInParcels: number, countryCode: number) => {
    const lens = getReadOnlyContract("ecolens");
    if (!lens) {
      throw new Error("EcoLens contract not found");
    }
    const quote = await lens.verificationTotalFor(BigInt(landSizeInParcels), BigInt(countryCode));
    return {
      total: BigInt(quote.total.toString()),
      scaled: BigInt(quote.scaled.toString()),
      attestation: BigInt(quote.attestation.toString()),
      judgment: BigInt(quote.judgment.toString()),
    };
  };

  const updateVerificationFee = async (landSizeInParcels: number) => {
    try {
      if (landSizeInParcels > 0) {
        const countryCode = resolveCountryCode(_formAPI.getState().values);
        const quote = await quoteVerificationFee(landSizeInParcels, countryCode);
        setVerificationFee(fromWei(quote.total.toString(), "ether"));
        setVerificationFeeParts({
          scaled: fromWei(quote.scaled.toString(), "ether"),
          attestation: fromWei(quote.attestation.toString(), "ether"),
          judgment: fromWei(quote.judgment.toString(), "ether"),
        });
      }
    } catch (e: any) {
      console.error("❌ [VERIFICATION FEE] Error fetching verification fee:", {
        error: e.message || e,
        parcels: landSizeInParcels,
      });
    }
  };

  const updateMaxContractPeriod = async (landSizeInParcels: number) => {
    console.log("🔗 [BLOCKCHAIN] updateMaxContractPeriod called with:", {
      landSizeInParcels,
      hasLibrary: !!library,
      currentContractMax: contractMax,
    });

    try {
      if (library && landSizeInParcels > 0) {
        console.log("🔗 [BLOCKCHAIN] Getting MetaMask signer...");
        const signer = await getMetaMaskSigner();
        console.log("🔗 [BLOCKCHAIN] Signer obtained:", !!signer);

        console.log("🔗 [BLOCKCHAIN] Getting easement contract...");
        const treeContract = getContract("tree-token", signer);
        console.log("🔗 [BLOCKCHAIN] Contract obtained:", !!treeContract);

        if (treeContract) {
          console.log("🔗 [BLOCKCHAIN] Calling calculateMaxEasementPeriodForLandSize with parcels:", landSizeInParcels);
          const maxContractPeriod = await treeContract.calculateMaxEasementPeriodForLandSize(landSizeInParcels);
          console.log("🔗 [BLOCKCHAIN] Raw response from contract:", {
            maxContractPeriod: maxContractPeriod.toString(),
            type: typeof maxContractPeriod,
          });

          const maxContractPeriodInYears = Number(duration(Number(maxContractPeriod), "s").as(TERM_UNIT).toFixed(0));
          console.log("🔗 [BLOCKCHAIN] Converted to years:", {
            rawSeconds: Number(maxContractPeriod),
            convertedYears: maxContractPeriodInYears,
            previousMax: contractMax,
          });

          setContractMax(maxContractPeriodInYears);
          console.log(
            "✅ [BLOCKCHAIN] Successfully updated max contract period to:",
            maxContractPeriodInYears,
            "years for land size:",
            landSizeInParcels,
            "parcels"
          );
        } else {
          console.warn("⚠️ [BLOCKCHAIN] Easement contract not available");
        }
      } else {
        console.warn("⚠️ [BLOCKCHAIN] Skipping update - library:", !!library, "parcels:", landSizeInParcels);
      }
    } catch (e: any) {
      console.error("❌ [BLOCKCHAIN] Error updating max contract period:", {
        error: e.message || e,
        parcels: landSizeInParcels,
        hasLibrary: !!library,
      });
      consoleError(e);
    }
  };

  const handleUnitChange = (e: any, form: FormApi<ScanValues>) => {
    const unit = e.target.value;
    const parcel = convertToParcels(Number(sizeValue) || 0, unit);
    setSizeUnit(unit);
    form.mutators.setParcels(parcel);
    console.log("Unit changed to:", unit, "with parcels:", parcel);
    // Re-fetch verification fee if already past step 2
    if (activeStep >= 3 && parcel > 0) {
      updateVerificationFee(parcel);
    }
  };

  const handleSizeChange = (e: any, form: FormApi<ScanValues>) => {
    const parcel = convertToParcels(Number(e.target.value), sizeUnit);
    setSizeValue(Number(e.target.value));
    setSizeParcels(parcel);
    form.mutators.setSize(`${Number(e.target.value)},${sizeUnit}`);
    form.mutators.setParcels(parcel);
    console.log("Size changed to:", e.target.value, "with unit:", sizeUnit, "and parcels:", parcel);
    // Re-fetch verification fee if already past step 2
    if (activeStep >= 3 && parcel > 0) {
      updateVerificationFee(parcel);
    }
  };

  // Zone management functions

  // Parse zone size string to get numeric value in current unit
  // const parseZoneSize = (sizeStr: string): { value: number; unit: string } | null => {
  //   const parts = sizeStr.split(",");
  //   if (parts.length !== 2) return null;
  //   const value = parseFloat(parts[0]);
  //   const unit = parts[1].trim();
  //   if (isNaN(value) || value <= 0) return null;
  //   return { value, unit };
  // };

  // Calculate total area from all zones in current unit

  // Validate zone size format

  const validateParcels = (values: ScanValues) => {
    if (values.size && values.size < 0) {
      return { size: `Size should be positive value` };
    } else if (values.parcels < sizeMin || values.parcels > sizeMax) {
      return { size: `Size in parcels should be between ${sizeMin} and ${sizeMax}` };
    } else {
      return {};
    }
  };

  const [codes, setCodes] = useState([]);

  const getCountryCodes = async () => {
    try {
      const res = await axios.get(ApiRoutes.user.getAvailableCountries).then((res: any) => res.data);

      const sortedCountries = res.sort((a: any, b: any) => {
        if (a.name.common < b.name.common) {
          return -1;
        }
        if (a.name.common > b.name.common) {
          return 1;
        }
        return 0;
      });

      setCodes(
        sortedCountries.map((country: any) => {
          return {
            label: country.name.common + " | " + country.idd.root + country.idd.suffixes[0],
            value: country.idd.root + country.idd.suffixes[0],
            icon: country.flags?.svg,
          };
        })
      );
    } catch (e: any) {
      console.error(e);
    }
  };

  useEffect(() => {
    getCountryCodes();
    loadDraftToken(_formAPI);
  }, [searchParams, user]);

  useEffect(() => {
    getCountriesList(axios, setCountriesList);
    loadBlogContent();
  }, []);

  useEffect(() => {
    getLandSizeLimits();
  }, [library]);

  useEffect(() => {
    // Fetch available countries
    axios
      .get(ApiRoutes.user.getAvailableCountries)
      .then((response) => {
        console.log("Available Countries Response:", response.data);
        setAvailableCountries(response.data);
      })
      .catch((error) => {
        console.error("Failed to fetch available countries:", error);
      });
  }, []);

  useEffect(() => {
    setSizeParcels(convertToParcels(Number(sizeValue) || 0, sizeUnit));
  }, [sizeUnit]);

  const getLandSizeLimits = async () => {
    try {
      if (library) {
        const signer = await getMetaMaskSigner();
        const contract = getContract("tree-token", signer);
        const easementContract = getContract("ecofutures-core", signer);
        if (contract && easementContract) {
          const min = await contract.minimumEasementLandSize();
          const max = await contract.maximumEasementLandSize();
          // Tree.calculateMaxEasementPeriodForLandSize is INVERSE to land size
          // (max_time = MAX_TREE_TOKENS / (mint_rate x land_units)), and the Fibonacci year cap
          // (3 years at level 1) only kicks in at the maximum land size. Quoting it for `max`
          // therefore returned the TIGHTEST bound the contract can produce — 3 on the accelerated
          // testnet — and every loaded draft with a longer period failed validation on the spot.
          // Seed with the loosest bound (smallest land) instead; the land's own bound replaces it
          // in updateMaxContractPeriod once the size is known (step 2 -> 3).
          const maxContract = await contract.calculateMaxEasementPeriodForLandSize(min);
          const minContract = await easementContract.minimumEasementPeriod();
          // const resMax = duration(Number(maxContract), "s").asDays();
          // const resMin = duration(Number(minContract), "s").asDays();
          const resMax = Number(duration(Number(maxContract), "s").as(TERM_UNIT).toFixed(0));
          const resMin = Number(duration(Number(minContract), "s").as(TERM_UNIT).toFixed(0));
          setContractMax(resMax);
          setContractMin(resMin);
          setSizeMax(Number(max));
          setSizeMin(Number(min));
        }
      }
    } catch (e: any) {
      console.log({ e });
      consoleError(e);
    }
  };

  // The "per hectare" figure shown in the copy. Admin.verificationFeePerHectare is the GLOBAL
  // base rate only — V10.1 prices V per country (setCountryRates) and folds in the flat deed
  // check + D-scaled judgment, so the raw getter no longer matches what one hectare costs.
  // Quote one hectare (100 parcels) through verificationFeeFor at the form's country instead;
  // country code 0 falls back to the global rate, same as the pull.
  const getVerificationFeePerHectare = async (countryCode: number) => {
    try {
      const adminContract = getReadOnlyContract("ecofutures-admin");
      if (adminContract) {
        const feePerHectare = await adminContract.verificationFeeFor(BigInt(100), BigInt(countryCode));
        setVerificationFeePerHectare(fromWei(feePerHectare.toString(), "ether"));
      }
    } catch (e: any) {
      console.log({ e });
      consoleError(e);
    }
  };

  const requestVerification = async (id: string): Promise<any> => {
    const response = await axios
      .post(ApiRoutes.user.profile.tokens.requestVerification, { id })
      .then((res) => res.data)
      .catch((error) => ({ failed: true, error }));

    if (response.failed) {
      return requestVerification(id);
    } else {
      return response;
    }
  };
  const getList = (array: string[] | string) => {
    if (!array) {
      return [];
    }
    let items: string[] = [];
    if (typeof array === "string") {
      // Assuming string values are comma-separated or single items
      // This might need adjustment based on how strings are stored in draftToken
      items = array.includes(",") ? array.split(",").map((item) => item.trim()) : [array];
    } else if (Array.isArray(array) && array.length > 0 && typeof array[0] === "string") {
      // This handles the case where it's already a flat array of strings e.g. from basicPerks
      items = array;
    } else if (Array.isArray(array) && array.length > 0 && Array.isArray(array[0])) {
      // This handles the original logic for nested arrays
      items = (array[0] as any).map((item: any) => item.label || item);
    } else if (
      Array.isArray(array) &&
      array.length > 0 &&
      typeof array[0] === "object" &&
      array[0] !== null &&
      "label" in array[0]
    ) {
      // Handles an array of objects with a label property
      items = array.map((item: any) => item.label);
    }

    return items.map((item: any, index: number) => {
      return {
        key: index,
        label: typeof item === "string" ? item : item.label, // Ensure label is a string
      };
    });
  };

  const approveCollection = async () => {
    setApproveStatus("processing");
    setError("");
    const values = _formAPI.getState().values;
    try {
      const signer = await getMetaMaskSigner();
      const contract = getContract("ecofutures-admin", signer);
      const erc20Contract = getContract("erc20", signer);

      if (!contract) throw new Error("ecofutures-admin contract not found!");
      if (!erc20Contract) throw new Error("erc20 contract not found!");

      const landSize = Number(values.parcels);
      // Approve the TOTAL — requestVerification pulls all three components in one transfer, so
      // an allowance covering only part of it reverts the request.
      const countryCode = resolveCountryCode(values);
      const verificationFee = (await quoteVerificationFee(landSize, countryCode)).total;
      console.log(
        "✅ [BLOCKCHAIN] approveCollection — verificationFee total:",
        fromWei(verificationFee.toString(), "ether"),
        { landSize, countryCode }
      );

      const userAddress = await signer.getAddress();
      const balance = await erc20Contract.balanceOf(userAddress);
      console.log("User Balance", fromWei(balance.toString(), "ether"));

      if (BigInt(balance.toString()) < BigInt(verificationFee.toString())) {
        throw new Error("Insufficient funds in wallet. Please add enough FREE tokens.");
      }

      const allowance = await erc20Contract.allowance(userAddress, ecoFuturesAdminContractAddress);
      console.log("User Allowance", fromWei(allowance.toString(), "ether"));
      if (BigInt(allowance.toString()) < BigInt(verificationFee.toString())) {
        const difference = BigInt(verificationFee.toString()) - BigInt(allowance.toString());
        console.log("🔗 [BLOCKCHAIN] approveCollection — increaseAllowance params:", {
          spender: ecoFuturesAdminContractAddress,
          differenceAmount: difference.toString(),
        });
        const allowanceTx = await erc20Contract.increaseAllowance(
          ecoFuturesAdminContractAddress,
          difference.toString()
        );
        console.log("⏳ [BLOCKCHAIN] approveCollection — waiting for allowance tx:", allowanceTx.hash);
        const allowanceReceipt = await checkTransactionConfirmationReceipt(allowanceTx.hash);
        console.log("✅ [BLOCKCHAIN] approveCollection — allowance tx confirmed:", allowanceReceipt);
      } else {
        console.log("✅ [BLOCKCHAIN] approveCollection — allowance already sufficient.");
      }

      setApproveStatus("success");
    } catch (e: any) {
      consoleError({ e });
      setError(e.message || "Approval failed.");
      setApproveStatus("error");
    }
  };

  const onSubmit = async (values: { [key: string]: any }) => {
    setLoading(true);
    setConfirmStatus("processing");
    setError("");

    try {
      if (isRejected) {
        // Verification was previously rejected — skip all blockchain calls and
        // go straight to the API-only re-submission flow.
        console.log("ℹ️ [onSubmit] Verification is rejected — skipping blockchain calls.");
        setConfirmStatus("success");

        const body = {
          tokenId: values.tokenId,
        };

        console.log("🌐 [API] onSubmit (rejected re-submit) — POST token create, body:", body);
        const response = await axios.post(ApiRoutes.token.create, body).then((res: AxiosResponse<any>) => res.data);
        console.log("✅ [API] onSubmit (rejected re-submit) — token create response:", response);

        const verificationRequest = await requestVerification(response.id);
        if (verificationRequest) {
          setOpen1(true);
        } else {
          throw new Error("Failed to request land verification!");
        }
        return;
      }

      const signer = await getMetaMaskSigner();
      const contract = getContract("ecofutures-admin", signer);
      if (!contract) throw new Error("ecofutures-admin contract not found!");

      const landSize = Number(values.parcels);
      const easementPeriodSeconds = Math.round(duration(Number(values.contract_period), TERM_UNIT).asSeconds());
      const tokenId = values?.tokenId?.toString() || "";

      const locationRaw = values?.location;
      const locationObj = typeof locationRaw === "string" ? JSON.parse(locationRaw) : locationRaw;
      const locationCountryCode = resolveCountryCode(values);

      // INTERIM parcelHash (V9.1): the contract enforces global uniqueness via
      // _parcelExists[parcelHash], so a constant (e.g. the zero hash) would permanently
      // block every request after the first. Until the surveyed-geometry derivation rule
      // is agreed (pending Azeem), hash the parcel's stable identifiers — the same parcel
      // always yields the same hash (duplicate registration stays blocked) while distinct
      // parcels never collide. There's also a requestVerificationFiat variant (adds a
      // `guardian` address) — worth checking whether guardian-purchased land should branch here.
      const parcelHash = keccakId(
        JSON.stringify({ tokenId, countryCode: locationCountryCode, landSize, location: locationObj })
      );

      console.log("🔗 [BLOCKCHAIN] onSubmit — requestVerification params:", {
        landSize,
        easementPeriodSeconds,
        tokenId,
        locationCountryCode,
        parcelHash,
      });
      const payVerification = await contract.requestVerification(
        landSize,
        easementPeriodSeconds,
        tokenId,
        locationCountryCode,
        parcelHash
      );
      console.log("⏳ [BLOCKCHAIN] onSubmit — waiting for requestVerification tx:", payVerification.hash);
      const r = await checkTransactionConfirmationReceipt(payVerification.hash);
      console.log("✅ [BLOCKCHAIN] onSubmit — requestVerification receipt:", r);

      const receipt = r as any;
      if (receipt?.status === "0" || receipt?.status === 0 || receipt?.status === false) {
        throw new Error("Transaction failed on-chain.");
      }

      setConfirmStatus("success");

      const body = {
        tokenId: values.tokenId,
      };

      console.log("🌐 [API] onSubmit — POST token create, body:", body);
      const response = await axios.post(ApiRoutes.token.create, body).then((res: AxiosResponse<any>) => res.data);
      console.log("✅ [API] onSubmit — token create response:", response);

      const verificationRequest = await requestVerification(response.id);
      if (verificationRequest) {
        setOpen1(true);
      } else {
        throw new Error("Failed to request land verification!");
      }
    } catch (e: any) {
      consoleError({ e });
      setError(e.message + (e.response?.data?.message ? ". " + e.response.data.message : ""));
      setConfirmStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const validateRef = useRef(validateParcels);
  validateRef.current = validateParcels;

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const _formAPI = useMemo(
    () =>
      createForm<ScanValues>({
        validateOnBlur: false,
        validate: (values) => validateRef.current(values),
        initialValues: initialValues,
        mutators: {
          setSize: (values, state, { changeValue }) => {
            console.log("here", values);
            changeValue(state, "size", () => values[0]);
          },
          setParcels: (values, state, { changeValue }) => {
            changeValue(state, "parcels", () => values[0]);
          },
          setDeed: (values, state, { changeValue }) => {
            changeValue(state, "deedCheck", () => values[0]);
          },
          setLocation: (values, state, { changeValue }) => {
            const old = state.lastFormState?.values.location || {};
            changeValue(state, "location", () => Object.assign(old, values[0]));
          },
          setElevationMin: (values, state, { changeValue }) => {
            changeValue(state, "elevationMin", () => values[0]);
          },
          setElevationMax: (values, state, { changeValue }) => {
            changeValue(state, "elevationMax", () => values[0]);
          },
          setAdditionalContacts: (values, state, { changeValue }) => {
            changeValue(state, "additional_contacts", () => values[0]);
          },
        },
        onSubmit: (values) => onSubmitRef.current(values),
      }),
    []
  );

  // Check if selected country is in available countries
  useEffect(() => {
    const selectedCountry = _formAPI.getState().values.location?.country;

    if (!selectedCountry) {
      setTrusteeInCountry(false);
      return;
    }

    const isAvailable = availableCountries.some(
      (country: any) => country.name.common === selectedCountry || country.name.official === selectedCountry
    );

    setTrusteeInCountry(isAvailable);
    console.log(`Trustee in ${selectedCountry}:`, isAvailable);
  }, [availableCountries, _formAPI.getState().values.location?.country, activeStep]);

  // Re-quote the per-hectare figure whenever the guardian picks a different country — V10.1
  // prices the rate per country, so the copy would otherwise show the wrong number.
  useEffect(() => {
    getVerificationFeePerHectare(resolveCountryCode(_formAPI.getState().values));
  }, [_formAPI.getState().values.location?.country]);

  // The contract_period field validator closes over contractMin/contractMax, but final-form only
  // re-runs validators on a value change — it has no idea the bounds moved. A draft's stored
  // period is validated once at initialize() against whatever bounds happened to be current
  // (a race against the chain reads above), and that verdict then sticks until the guardian
  // edits something. setConfig("validate") is the one public hook that forces a full
  // runValidation (record + every field), so re-arm it whenever the bounds change.
  useEffect(() => {
    _formAPI.setConfig("validate", (values) => validateRef.current(values));
  }, [_formAPI, contractMin, contractMax]);

  const loadDraftToken = async (formAPI: FormApi<ScanValues>) => {
    try {
      setIsDraftLoading(true);
      // Wait for user to be available before proceeding
      if (!user) {
        console.log("User not available yet, skipping loadDraftToken");
        setIsDraftLoading(false);
        return;
      }

      // Check if tokenId is provided in URL parameters
      const urlTokenId = searchParams.get("tokenId");

      if (urlTokenId) {
        try {
          const draftToken = await (await axios.get(ApiRoutes.token.draftId(urlTokenId))).data;
          if (draftToken) {
            setTokenId(urlTokenId);
            setIsRejected(draftToken.verifications?.[0]?.status === "rejected");
            const meta = draftToken.meta || {};
            let location = meta.location;
            setPreview(draftToken.content || null);
            console.log("Draft token loaded:", draftToken.content);
            if (typeof location === "string") {
              try {
                location = JSON.parse(location);
              } catch {
                location = { name: "", lat: "", lon: "", country: "" };
              }
            }

            if (draftToken.files && draftToken.files.length > 0) {
              const deedFile = draftToken.files.find((file: any) => file.type === "deed");
              if (deedFile && deedFile.url) {
                try {
                  const deedFileObj = {
                    name: deedFile.name,
                    url: deedFile.url,
                    type: "application/pdf",
                    isUrlOnly: true,
                  };
                  setDeedFile(deedFileObj as any);
                } catch (err) {
                  console.error("Failed to process deed file URL:", err);
                }
              }

              const surveyFile = draftToken.files.find((file: any) => file.type === "survey_plan");
              if (surveyFile && surveyFile.url) {
                try {
                  const surveyFileObj = {
                    name: surveyFile.name,
                    url: surveyFile.url,
                    type: "application/pdf",
                    isUrlOnly: true,
                  };
                  setSurvey(surveyFileObj as any);
                } catch (err) {
                  console.error("Failed to load survey plan file:", err);
                }
              }
            }

            if (draftToken.content) {
              try {
                // Store URL directly rather than downloading the file
                const contentUrlString =
                  typeof draftToken.content === "string" ? draftToken.content : (draftToken.content as any)?.url;

                let contentName: string | undefined;
                if (typeof draftToken.content === "string") {
                  contentName = draftToken.content.substring(draftToken.content.lastIndexOf("/") + 1) || "content_file";
                } else {
                  contentName = (draftToken.content as any)?.name;
                }

                // Attempt to get contentType from draftToken.contentType if content is a string,
                // or from draftToken.content.type if content is an object.
                let contentType: string | undefined;
                if (typeof draftToken.content === "string") {
                  contentType = (draftToken as any).contentType; // Assumes contentType might be a sibling field on draftToken
                } else {
                  contentType = (draftToken.content as any)?.type;
                }

                const contentFileObj = {
                  name: contentName,
                  url: contentUrlString,
                  type: contentType,
                  isUrlOnly: true, // Flag to indicate this is just a URL reference, not an actual File object
                };
                setContent(contentFileObj as any);
              } catch (err) {
                console.error("Failed to load content file:", err);
              }
            }

            if (draftToken?.easements && Array.isArray(draftToken?.easements)) {
              setEasementCount(draftToken?.easements.length);
            }

            if (meta.size) {
              const size = meta.size.split(",")[0];
              const unit = meta.size.split(",")[1];
              formAPI.mutators.setSize(`${Number(size)},${unit}`);
              handleUnitChange({ target: { value: unit } }, formAPI);
              handleSizeChange({ target: { value: size } }, formAPI);
              setSizeUnit(unit as Unit);
              setSizeValue(Number(size));
              validateParcels(formAPI.getState().values);
            }

            // Load zones from draft if available
            // if (meta.zones && Array.isArray(meta.zones)) {
            //   setZones(meta.zones);
            // }

            formAPI.initialize({
              ...initialValues,
              tokenId: draftToken.id || urlTokenId || null,
              name: meta.name || "",
              description: meta.description || "",
              content: draftToken.content || null,
              elevationMin: meta.elevationMin ?? 0,
              elevationMax: meta.elevationMax ?? 0,
              location: location || { name: "", lat: "", lon: "", country: "" },
              biomes: meta.biomes?.join(",") || "",
              flora: meta.flora ? getList([meta.flora]) : [],
              fauna: meta.fauna ?? "",
              dwellings: meta.dwellings ? getList([meta.dwellings]) : [],
              production: meta.production ? getList([meta.production]) : [],
              bfd_plan: meta.bfd_plan ?? "",
              basicPerks: meta.basicPerks ? getList(meta.basicPerks) : [],
              contract_period: meta.contractPeriod ?? undefined,
              startingBid: meta.startingBid ? fromWei(meta.startingBid, "ether") : "",
              size: meta.size ? meta.size.split(",")[0] : undefined,
              parcels: meta.size
                ? convertToParcels(Number(meta.size.split(",")[0]), meta.size.split(",")[1] as Unit)
                : 0,
              additional_contacts: Array.isArray(meta.additional_contacts)
                ? [
                    meta.additional_contacts[0] || "",
                    meta.additional_contacts[1] && meta.additional_contacts[1].includes("-")
                      ? meta.additional_contacts[1].split("-")[1]
                      : meta.additional_contacts[1] || "",
                    meta.additional_contacts[2] || user?.email || "",
                    meta.additional_contacts[3] || "",
                  ]
                : [],
              phoneCode:
                Array.isArray(meta.additional_contacts) &&
                meta.additional_contacts[1] &&
                meta.additional_contacts[1].includes("-")
                  ? meta.additional_contacts[1].split("-")[0] || ""
                  : "",
              deedCheck: meta.deedCheck ? meta.deedCheck : false,
              easements:
                draftToken?.easements
                  ?.sort((a: any, b: any) => a.level - b.level)
                  .map((easement: any) => ({
                    description: easement.description || "",
                    activationBid: easement.activationBid ? fromWei(easement.activationBid, "ether") : "0",
                  })) || [],
              files: draftToken.files || [],
            });

            formAPI.restart();

            // Initialize lastSavedValuesRef with the loaded values
            if (lastSavedValuesRef) {
              lastSavedValuesRef.current = JSON.parse(JSON.stringify(formAPI.getState().values));
              console.log("[DEBUG] lastSavedValuesRef initialized from loaded draft");
            }

            setInitialDwellings(getList([meta.dwellings]));
            setInitialFlora(getList([meta.flora]));
            setInitialProduction(getList([meta.production]));
            setInitialPerks(getList(meta.basicPerks));
            setInitialPerks1(getList(draftToken.easements[0]?.perks || []));
            setInitialPerks2(getList(draftToken.easements[1]?.perks || []));
            setNewDraft(false);
            console.log("Form initial values", formAPI.getState().initialValues);
            return;
          } else {
            console.error("Token not found for ID:", urlTokenId);
            formAPI.initialize({
              ...initialValues,
              additional_contacts: ["", "", user?.email || "", ""],
            });
            setNewDraft(true);
            return;
          }
        } catch (error) {
          console.error("Failed to load token from URL parameter:", urlTokenId, error);
          // Fall back to loading latest draft if URL token fails
        }
      }

      const response = await axios.get(ApiRoutes.user.profile.tokens.root);
      const latestDraftToken = response.data.find((token: any) => token.status === "draft" && token.trust === null);

      if (!latestDraftToken) {
        formAPI.initialize({
          ...initialValues,
          additional_contacts: ["", "", user?.email || "", ""],
        });
        setNewDraft(true);
        return;
      }

      const draftToken = await axios
        .get(ApiRoutes.token.draftId(latestDraftToken.id))
        .then((res: AxiosResponse<any>) => res.data);
      if (draftToken) {
        setTokenId(latestDraftToken.id);
        const meta = draftToken.meta || {};
        let location = meta.location;

        if (typeof location === "string") {
          try {
            location = JSON.parse(location);
          } catch {
            location = { name: "", lat: "", lon: "", country: "" };
          }
        }

        if (draftToken.files && draftToken.files.length > 0) {
          const deedFile = draftToken.files.find((file: any) => file.type === "deed");
          if (deedFile && deedFile.url) {
            try {
              const deedFileObj = {
                name: deedFile.name,
                url: deedFile.url,
                type: "application/pdf",
                isUrlOnly: true,
              };
              setDeedFile(deedFileObj as any);
            } catch (err) {
              console.error("Failed to process deed file URL:", err);
            }
          }

          const surveyFile = draftToken.files.find((file: any) => file.type === "survey_plan");
          if (surveyFile && surveyFile.url) {
            try {
              const surveyFileObj = {
                name: surveyFile.name,
                url: surveyFile.url,
                type: "application/pdf",
                isUrlOnly: true,
              };
              setSurvey(surveyFileObj as any);
            } catch (err) {
              console.error("Failed to load survey plan file:", err);
            }
          }
        }

        if (draftToken.content) {
          try {
            // Store URL directly rather than downloading the file
            const contentUrlString =
              typeof draftToken.content === "string" ? draftToken.content : (draftToken.content as any)?.url;
            setPreview(contentUrlString);
            let contentName: string | undefined;
            if (typeof draftToken.content === "string") {
              contentName = draftToken.content.substring(draftToken.content.lastIndexOf("/") + 1) || "content_file";
            } else {
              contentName = (draftToken.content as any)?.name;
            }

            // Attempt to get contentType from draftToken.contentType if content is a string,
            // or from draftToken.content.type if content is an object.
            let contentType: string | undefined;
            if (typeof draftToken.content === "string") {
              contentType = (draftToken as any).contentType; // Assumes contentType might be a sibling field on draftToken
            } else {
              contentType = (draftToken.content as any)?.type;
            }

            const contentFileObj = {
              name: contentName,
              url: contentUrlString,
              type: contentType,
              isUrlOnly: true, // Flag to indicate this is just a URL reference, not an actual File object
            };
            setContent(contentFileObj as any);
          } catch (err) {
            console.error("Failed to load content file:", err);
          }
        }
        if (draftToken?.easements && Array.isArray(draftToken?.easements)) {
          setEasementCount(draftToken?.easements.length);
        }

        if (meta.size) {
          const size = meta.size.split(",")[0];
          const unit = meta.size.split(",")[1];
          console.log("Setting size and unit from meta:", size, unit);
          handleUnitChange({ target: { value: unit } }, formAPI);
          handleSizeChange({ target: { value: size } }, formAPI);
          formAPI.mutators.setSize(`${Number(size)},${unit}`);
          validateParcels(formAPI.getState().values);
        }
        formAPI.initialize({
          ...initialValues,
          tokenId: draftToken.id || latestDraftToken.id || null,
          name: meta.name || "",
          description: meta.description || "",
          content: draftToken.content || null,
          elevationMin: meta.elevationMin ?? 0,
          elevationMax: meta.elevationMax ?? 0,
          location: location || { name: "", lat: "", lon: "", country: "" },
          biomes: meta.biomes?.join(",") || "",
          flora: meta.flora ? getList([meta.flora]) : [],
          fauna: meta.fauna ?? "",
          dwellings: meta.dwellings ? getList([meta.dwellings]) : [],
          production: meta.production ? getList([meta.production]) : [],
          bfd_plan: meta.bfd_plan ?? "",
          size: meta.size ? meta.size : undefined,
          parcels: meta.size ? convertToParcels(Number(meta.size.split(",")[0]), meta.size.split(",")[1] as Unit) : 0,
          basicPerks: meta.basicPerks ? getList(meta.basicPerks) : [],
          contract_period: meta.contractPeriod ?? undefined,
          startingBid: meta.startingBid ? fromWei(meta.startingBid, "ether") : "",
          additional_contacts: Array.isArray(meta.additional_contacts)
            ? [
                meta.additional_contacts[0] || "",
                meta.additional_contacts[1] && meta.additional_contacts[1].includes("-")
                  ? meta.additional_contacts[1].split("-")[1]
                  : meta.additional_contacts[1] || "",
                meta.additional_contacts[2] || user?.email || "",
                meta.additional_contacts[3] || "",
              ]
            : [],
          phoneCode:
            Array.isArray(meta.additional_contacts) &&
            meta.additional_contacts[1] &&
            meta.additional_contacts[1].includes("-")
              ? meta.additional_contacts[1].split("-")[0] || ""
              : "",
          deedCheck: meta.deedCheck ? meta.deedCheck : false,
          easements:
            draftToken?.easements
              ?.sort((a: any, b: any) => a.level - b.level)
              .map((easement: any) => ({
                description: easement.description || "",
                activationBid: easement.activationBid ? fromWei(easement.activationBid, "ether") : "0",
              })) || [],
          files: draftToken.files || [],
        });

        // Force fields to update with new initial values
        formAPI.reset();

        formAPI.mutators.setAdditionalContacts([
          meta.additional_contacts[0] || "",
          meta.additional_contacts[1] && meta.additional_contacts[1].includes("-")
            ? meta.additional_contacts[1].split("-")[1]
            : meta.additional_contacts[1] || "",
          meta.additional_contacts[2] || user?.email || "",
          meta.additional_contacts[3] || "",
        ]);

        // Initialize lastSavedValuesRef with the loaded values
        if (lastSavedValuesRef) {
          lastSavedValuesRef.current = JSON.parse(JSON.stringify(formAPI.getState().values));
          console.log("[DEBUG] lastSavedValuesRef initialized from latest draft");
        }

        setInitialDwellings(getList([meta.dwellings]));
        setInitialFlora(getList([meta.flora]));
        setInitialProduction(getList([meta.production]));
        setInitialPerks(getList(meta.basicPerks));
        setInitialPerks1(getList(draftToken.easements[0]?.perks || []));
        setInitialPerks2(getList(draftToken.easements[1]?.perks || []));
        // Ensure we're setting the tokenId consistently
        const tokenIdToSet = draftToken.tokenId || draftToken.id || latestDraftToken.id;
        setTokenId(tokenIdToSet);
        setNewDraft(false);

        // Initialize uploaded files tracking from existing draft files
        const existingUploadedFiles: any = {};
        if (draftToken.files && Array.isArray(draftToken.files)) {
          draftToken.files.forEach((file: any) => {
            if (file.type === "survey_plan") {
              existingUploadedFiles.surveyPlan = { name: file.name, size: file.size };
            } else if (file.type === "deed") {
              existingUploadedFiles.deed = { name: file.name, size: file.size };
            }
          });
        }
        setUploadedFiles(existingUploadedFiles);

        console.log("Form initial values", formAPI.getState().initialValues);
      } else {
        formAPI.initialize({
          ...initialValues,
          additional_contacts: ["", "", user?.email || "", ""],
        });
        setNewDraft(true);
      }
    } catch (error) {
      console.error("Failed to load draft token:", error);
      // Initialize with user email even on error
      formAPI.initialize({
        ...initialValues,
        additional_contacts: ["", "", user?.email || "", ""],
      });
      setNewDraft(true);
    } finally {
      setIsDraftLoading(false);
    }
  };

  const printContactFields = () => {
    return (
      <>
        <ContactRow sx={{ mb: { xs: 0, md: 0 } }}>
          <Field
            name={`additional_contacts[0]`}
            type="text"
            component={TextFieldControlSimple}
            label="Type name here"
            validate={FormValidators.required()}
          />
        </ContactRow>
        <Section title="Contact Information" />
        <SectionRow sx={{ gap: { xs: "16px", md: "20px" } }}>
          <ContactRow sx={{ mb: 0 }}>
            <Field
              name={"phoneCode"}
              type="text"
              component={SelectFieldControl}
              label="Select country code"
              renderIcon={(item: any) => <img width="20px" src={item.icon} alt={item.label} />}
              options={codes} // Make sure to define and fetch the country codes as in BecomeTrust.tsx
              validate={FormValidators.composeValidators(
                FormValidators.required(),
                FormValidators.doesRegexMatch(/^\+\d{1,3}$/, "Please enter a valid country code")
              )}
            />
          </ContactRow>

          <ContactRow sx={{ mb: 0 }}>
            <Field
              name={`additional_contacts[1]`}
              type="text"
              component={TextFieldControlSimple}
              label="Type phone number here"
              validate={FormValidators.composeValidators(
                FormValidators.required(),
                FormValidators.doesRegexMatch(
                  /^(\+?\d{1,3}[\s.-]?)?(\(?\d{1,4}\)?[\s.-]?)?[\d\s.-]{7,15}$/,
                  "Please enter valid phone number"
                )
              )}
            />
          </ContactRow>
          <ContactRow sx={{ visibility: "hidden", display: "none" }}>
            <Field
              name={`additional_contacts[2]`}
              type="text"
              component={TextFieldControlSimple}
              value={user?.email}
              defaultValue={user?.email}
            />
          </ContactRow>
        </SectionRow>
        <ContactRow sx={{ mt: { xs: "18px", md: "24px" } }}>
          <Field
            name={`additional_contacts[3]`}
            type="text"
            component={TextFieldControlSimple}
            label="Type Contact Address here"
            validate={FormValidators.required()}
          />
        </ContactRow>
      </>
    );
  };

  const blog = blogs[5];
  const blogDescription = blog.description.replace(
    "{{VERIFICATION_FEE_PER_HECTARE}}",
    verificationFeePerHectare
      ? `$ ${parseFloat(verificationFeePerHectare).toLocaleString(undefined, {
          maximumFractionDigits: 4,
        })} ${CURRENCY_NAME}`
      : "…"
  );
  return (
    <LoadingScreen loading={isRequestLoading || isDraftLoading} showCircularLoader={true} loaderColor="white">
      <Wrapper sx={{ backgroundColor: "transparent", borderRadius: "20px" }}>
        <Form
          form={_formAPI}
          subscription={{
            touched: true,
            pristine: true,
            invalid: true,
            active: true,
            values: true,
            valid: true,
            validating: true,
            errors: true,
            modified: true,
            initialValues: true,
          }}
          onSubmit={onSubmit}
          render={({ handleSubmit, form, values, invalid }) => (
            <FormArea
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.target as any).nodeName !== "TEXTAREA") {
                  e.preventDefault();
                }
              }}
              sx={{ borderRadius: "20px", backgroundColor: "transparent" }}
            >
              {activeStep >= 0 && <ProgressStepper steps={steps} activeStep={activeStep} />}
              <Grid container direction="column" borderRadius={"20px"}>
                <Step
                  sx={{ p: sizing(6), paddingTop: sizing(0), backgroundColor: "back.light", borderRadius: "20px" }}
                  step={-1}
                  currentStep={activeStep}
                  nextStep={setStep}
                  form={form}
                  tokenId={tokenId}
                  newDraft={newDraft}
                  setTokenId={setTokenId}
                  axios={axios}
                  contentFile={contentFile}
                  surveyFile={surveyFile}
                  deedFile={deedFile}
                  setIsRequestLoading={setIsRequestLoading}
                  updateMaxContractPeriod={updateMaxContractPeriod}
                  lastSavedValuesRef={lastSavedValuesRef}
                  uploadedContentAliasRef={uploadedContentAliasRef}
                  hasFormDataChanged={hasFormDataChanged}
                >
                  {/* {isMobile ? (
                  <>
                    <Attention sx={{ borderBottomColor: "black", }}>Attention</Attention>
                    <Text sx={{ marginX: sizing(6), mt: sizing(6) }}>You cannot upload lands via Mobile</Text>
                  </>
                ) : ( */}
                  <Grid
                    width="100%"
                    flexDirection="column"
                    display={"flex"}
                    justifyContent={"space-between"}
                    sx={{
                      paddingX: { xs: sizing(4).xs, md: 0 },
                      background: "transparent",
                      color: "black",
                      margin: "auto",
                      // paddingLeft: { xs: "0", sm: "15px", md: "120px" },
                    }}
                  >
                    <Attention
                      inverted
                      sx={{
                        justifyContent: "space-between",
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "flex-start", md: "baseline" },
                        border: "none",
                        paddingY: sizing(0),
                        mb: { xs: "15px", md: "20px" },
                        mt: { xs: "15px", md: "20px" },
                      }}
                    >
                      <Text
                        capital={false}
                        sx={{
                          font: "inherit",
                          fontSize: { xs: "20px", sm: "22px", md: "26px" },
                          fontWeight: "600",
                          color: "black",
                          width: { xs: "100%", md: "50%" },
                          lineHeight: { xs: 1.3, md: 1.5 },
                        }}
                      >
                        <div id="blogTitle">{blog.title}</div>
                      </Text>
                    </Attention>
                    <Text
                      sx={{
                        flexDirection: "column",
                        textDecoration: "none",
                        color: "black",
                        mb: { xs: "20px", md: "40px" },
                        width: { xs: "100%", sm: "100%", md: "80%" },
                        fontSize: "14px",
                        lineHeight: { xs: 1.4, md: 1.6 },
                      }}
                      dangerouslySetInnerHTML={{ __html: blogDescription }}
                    />
                  </Grid>
                  {/* )} */}
                </Step>

                {/*-Step 1-*/}
                <Step
                  sx={{ p: sizing(6), paddingTop: sizing(0), backgroundColor: "white", borderRadius: "20px" }}
                  step={0}
                  currentStep={activeStep}
                  nextStep={setStep}
                  form={form}
                  tokenId={tokenId}
                  newDraft={newDraft}
                  setTokenId={setTokenId}
                  axios={axios}
                  contentFile={contentFile}
                  surveyFile={surveyFile}
                  deedFile={deedFile}
                  sizeUnit={sizeUnit}
                  sizeValue={sizeValue}
                  setIsRequestLoading={setIsRequestLoading}
                  imagePreview={imagePreview}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  updateMaxContractPeriod={updateMaxContractPeriod}
                  lastSavedValuesRef={lastSavedValuesRef}
                  uploadedContentAliasRef={uploadedContentAliasRef}
                  hasFormDataChanged={hasFormDataChanged}
                >
                  <Section title="What's the name registered in the property deed?">{printContactFields()}</Section>
                  <Box
                    sx={{
                      mt: sizing(6),
                      px: sizing(3),
                      py: sizing(6),
                      backgroundColor: "#f5f5f5",
                      border: "2px dashed #ccc",
                      borderRadius: "12px",
                    }}
                  >
                    <Field
                      name="deedCheck"
                      type="checkbox"
                      component={CheckBoxFieldComponent}
                      label="I agree to present the trust with a legally binding deed or lease agreement that can be used to verify that I/my company holds clear ownership title of land within the jurisdiction of the country that the land is governed by."
                      validate={FormValidators.required("You must agree to present the deed document for verification")}
                    />
                  </Box>

                  {/*-Upload Image-*/}
                  {/* <Section title="Upload an image to illustrate or signify the land in GIF, PNG, or JEPG formats.
                 Make sure you have the right to use this image.">
                  <ContentUpload setFile={setContent} setPreview={setPreview} />
                </Section> */}
                </Step>
                {/*-Step 2-*/}
                <Step
                  btnSx={{ marginX: sizing(6), width: "auto" }}
                  sx={{ paddingBottom: { xs: "18px", md: "24px" }, backgroundColor: "white", borderRadius: "20px" }}
                  step={1}
                  currentStep={activeStep}
                  nextStep={setStep}
                  form={form}
                  tokenId={tokenId}
                  newDraft={newDraft}
                  setTokenId={setTokenId}
                  axios={axios}
                  contentFile={contentFile}
                  surveyFile={surveyFile}
                  deedFile={deedFile}
                  sizeUnit={sizeUnit}
                  sizeValue={sizeValue}
                  setIsRequestLoading={setIsRequestLoading}
                  imagePreview={imagePreview}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  updateMaxContractPeriod={updateMaxContractPeriod}
                  lastSavedValuesRef={lastSavedValuesRef}
                  uploadedContentAliasRef={uploadedContentAliasRef}
                  hasFormDataChanged={hasFormDataChanged}
                >
                  {/* <Attention sx={{ width: "auto", paddingX: sizing(6), backgroundColor: "back.light" }}>Step 2 of 4</Attention> */}
                  <Section
                    sx={{ paddingTop: sizing(6), backgroundColor: "white", border: "none", borderRadius: "20px" }}
                  >
                    <Text sx={{ marginX: sizing(6), mb: sizing(6) }}>Click the location of your land on the map.</Text>
                    <MapWithOverlay>
                      <CountryPrefiller
                        phoneCode={values.phoneCode}
                        currentCountry={values.location?.country}
                        codes={codes}
                        form={form}
                      />
                      <LocationGeocoder lat={values.location?.lat} lon={values.location?.lon} form={form} />
                      <LocationScrollTrigger
                        lat={values.location?.lat}
                        lon={values.location?.lon}
                        targetRef={locationOverlayRef}
                      />
                      <MapDialog
                        location={values.location}
                        handleChoose={handleMapChoose(form)}
                        height="50vh"
                        canvasBorderRadius="0 0 20px 20px"
                        sx={{
                          "@media (min-width: 768px)": {
                            height: "478px",
                            minHeight: "478px",
                            maxHeight: "478px",
                            "#map-box-container": {
                              height: "478px !important",
                            },
                            "#map-box-container canvas": {
                              height: "478px !important",
                              minHeight: "478px",
                              maxHeight: "478px",
                            },
                          },
                        }}
                      />
                      <Box
                        ref={locationOverlayRef}
                        sx={{
                          position: { xs: "static", md: "absolute" },
                          top: "24px",
                          left: "24px",
                          zIndex: 2,
                          width: { xs: "100%", md: "360px" },
                          maxWidth: { xs: "100%", md: "calc(100% - 48px)" },
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: { xs: "0", md: "20px" },
                          overflow: "hidden",
                          boxShadow: { xs: "none", md: "0px 8px 12px rgba(0,0,0,0.18)" },
                          opacity: !!(
                            values.location?.lat &&
                            values.location?.lon &&
                            Number(values.location.lat) !== 0 &&
                            Number(values.location.lon) !== 0
                          )
                            ? 1
                            : { xs: 1, md: 0 },
                          pointerEvents: !!(
                            values.location?.lat &&
                            values.location?.lon &&
                            Number(values.location.lat) !== 0 &&
                            Number(values.location.lon) !== 0
                          )
                            ? "auto"
                            : "none",
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#d1efff",
                            borderRadius: { xs: "0", md: "20px 20px 0 0" },
                            p: { xs: "18px", md: "20px 24px" },
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                          }}
                        >
                          <InfoIconOutlined sx={{ color: "#000", fontSize: "44px", flexShrink: 0, mt: "2px" }} />
                          <Text
                            sx={{
                              fontSize: "14px",
                              lineHeight: "19.2px",
                              color: "#000",
                              fontWeight: 400,
                              textTransform: "none",
                            }}
                          >
                            Please double&#8209;check the place name before continuing.
                          </Text>
                        </Box>
                        <LocationOverlayCard
                          visible={
                            !!(
                              values.location?.lat &&
                              values.location?.lon &&
                              Number(values.location.lat) !== 0 &&
                              Number(values.location.lon) !== 0
                            )
                          }
                          sx={
                            {
                              position: "relative",
                              top: "unset",
                              left: "unset",
                              zIndex: "unset",
                              width: "100%",
                              maxWidth: "100%",
                              borderRadius: 0,
                            } as any
                          }
                        >
                          <Section sx={{ pt: { xs: "12px", md: "0px" } }}>
                            <Field
                              label="Land location area / city / town"
                              formAPI={form}
                              name="location.name"
                              type="text"
                              maxLength={100}
                              component={TextFieldControlSimple}
                              validate={FormValidators.composeValidators(
                                FormValidators.required(""),
                                FormValidators.maxValue(100, "Location name must be 100 characters or less")
                              )}
                            />
                          </Section>
                          <Section sx={{ pt: { xs: "12px", md: "24px" } }}>
                            <Field
                              formAPI={form}
                              name="location.country"
                              label="Select country"
                              type="text"
                              component={SelectFieldControl}
                              options={countriesList}
                              renderIcon={(item: any) => <img width="20px" src={item.icon} alt={item.label} />}
                              validate={FormValidators.required("")}
                            />
                          </Section>
                          <SectionRow sx={{ marginTop: { xs: "12px", md: "0px" }, gap: { xs: "12px", md: "24px" } }}>
                            <Section sx={{ pt: 0 }}>
                              <Field
                                label="Land latitude"
                                formAPI={form}
                                name="location.lat"
                                type="number"
                                sx={monoFieldSx}
                                component={TextFieldControlSimple}
                                validate={FormValidators.composeValidators(
                                  FormValidators.required(""),
                                  FormValidators.inRange({ from: -90, to: 90 }, "Latitude should be between -90 and 90")
                                )}
                              />
                            </Section>
                            <Section sx={{ pt: 0 }}>
                              <Field
                                label={"Land longitude"}
                                formAPI={form}
                                name="location.lon"
                                type="number"
                                sx={monoFieldSx}
                                component={TextFieldControlSimple}
                                validate={FormValidators.composeValidators(
                                  FormValidators.required(""),
                                  FormValidators.inRange(
                                    { from: -180, to: 180 },
                                    "Longitude should be between -180 and 180"
                                  )
                                )}
                              />
                            </Section>
                          </SectionRow>
                          <Section sx={{ pt: { xs: "12px", md: "24px" } }}>
                            <Field
                              label={"DMS (Degrees, Minutes, Seconds)"}
                              formAPI={form}
                              name="location.dms"
                              type="text"
                              sx={monoFieldSx}
                              disabled={true}
                              component={TextFieldControlSimple}
                              input={{
                                value: convertToDMS(
                                  Number(values.location?.lat || 0),
                                  Number(values.location?.lon || 0)
                                ),
                                onChange: () => {
                                  /* No-op since field is disabled */
                                },
                                name: "location.dms",
                              }}
                            />
                          </Section>
                        </LocationOverlayCard>
                      </Box>
                    </MapWithOverlay>
                  </Section>
                </Step>

                {/*-Step 3-*/}
                <Step
                  sx={{ paddingX: sizing(6), paddingBottom: sizing(6), backgroundColor: "white", borderRadius: "20px" }}
                  step={2}
                  currentStep={activeStep}
                  nextStep={setStep}
                  form={form}
                  validationItems={[contentFile, surveyFile]}
                  tokenId={tokenId}
                  newDraft={newDraft}
                  setTokenId={setTokenId}
                  axios={axios}
                  contentFile={contentFile}
                  surveyFile={surveyFile}
                  deedFile={deedFile}
                  sizeUnit={sizeUnit}
                  sizeValue={sizeValue}
                  setIsRequestLoading={setIsRequestLoading}
                  imagePreview={imagePreview}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  updateMaxContractPeriod={updateMaxContractPeriod}
                  updateVerificationFee={updateVerificationFee}
                  lastSavedValuesRef={lastSavedValuesRef}
                  uploadedContentAliasRef={uploadedContentAliasRef}
                  hasFormDataChanged={hasFormDataChanged}
                >
                  {/* <Attention>Step 3 of 4</Attention> */}

                  <SectionRow
                    sx={{ flexDirection: { xs: "column", md: "row" }, gap: { xs: "18px", md: "24px" }, marginTop: 0 }}
                  >
                    <Section title="Name your land (publicly visible)">
                      <Field
                        formAPI={form}
                        placeholder="Type name here"
                        name="name"
                        type="text"
                        maxLength={255}
                        validate={FormValidators.composeValidators(
                          FormValidators.required(),
                          FormValidators.onlySpaces,
                          FormValidators.maxValue(255, "Name must be 255 characters or less")
                        )}
                        component={TextFieldControlSimple}
                      />
                    </Section>
                    <Section title="Size">
                      <InputWithOption>
                        <Field
                          formAPI={form}
                          name="size"
                          type="number"
                          sx={monoFieldSx}
                          component={TextFieldControlSimple}
                          input={{
                            onChange: (e: any) => {
                              handleSizeChange(e, form);
                            },
                            name: "size",
                            type: "number",
                            min: 0.01,
                            value: sizeValue,
                            step: 0.01,
                          }}
                          min={0.01}
                          validate={FormValidators.composeValidators(FormValidators.required("Land size is required"))}
                        />

                        <InputOption>
                          <Select
                            variant="outlined"
                            sx={{ fontFamily: "monospace" }}
                            value={sizeUnit}
                            onChange={(e: any) => handleUnitChange(e, form)}
                          >
                            {["hectares", "acres", "sqm", "sqft"].map((item, index) => {
                              return (
                                <MenuItem sx={{ fontFamily: "monospace" }} key={index} value={item}>
                                  {item}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <InputResult>{sizeParcels} parcels</InputResult>
                        </InputOption>
                      </InputWithOption>
                    </Section>
                  </SectionRow>

                  {/* <Grid
                    container
                    sx={{
                      display: "flex",
                      width: "100%",
                      flexDirection: { xs: "column", md: "row" },
                      mt: { xs: "24px", md: 0 },
                    }}
                    gap={{ xs: "18px", md: "24px" }}
                  > */}
                  {/* <Grid item xs={12} md={6}> */}
                  <SectionRow
                    sx={{ flexDirection: { xs: "column", md: "row" }, gap: { xs: "18px", md: "24px" }, marginTop: 0 }}
                  >
                    <Section title="Provide a general description of your land.">
                      <Field
                        formAPI={form}
                        placeholder="e.g.This is an uncultivated tropical land located in Sri Lanka,
                        with native tree species such as coconut, jackfruit, and mango.
                        The land has a natural environment with fertile soil and thriving undergrowth,
                         with potential for conservation or sustainable use."
                        name="description"
                        multiline={true}
                        type="text"
                        maxLength={255}
                        validate={FormValidators.composeValidators(
                          FormValidators.required(),
                          FormValidators.onlySpaces,
                          FormValidators.maxValue(255, "Description must be 255 characters or less")
                        )}
                        component={TextFieldControlSimple}
                      />
                      <CharCount value={values.description} max={255} />
                    </Section>
                    {/* </Grid>
                    <Grid item xs={12} md={6} sx={{ paddingTop: { xs: "0px", md: "18px" } }}> */}
                    <Section>
                      <DocUploadNew
                        file={surveyFile}
                        setFile={setSurvey}
                        title="MAX 100MB PDF Format"
                        heading="Upload land survey"
                        subheading="Make sure you have the rights to use this"
                      />
                    </Section>
                  </SectionRow>
                  {/* </Grid>
                  </Grid> */}
                  <SectionRow sx={{ flexDirection: { xs: "column", md: "row" }, marginTop: 0 }}>
                    <Section title="Enter lowest elevation" sx={{ paddingTop: { xs: 0, md: "24px" } }}>
                      <Field
                        formAPI={form}
                        name="elevationMin"
                        placeholder="Example 200 meters"
                        type="number"
                        sx={monoFieldSx}
                        input={{
                          onChange: (e: any) => {
                            form.mutators.setElevationMin(e.target.value);
                          },
                          name: "elevationMin",
                          type: "number",
                          value: values.elevationMin,
                          step: 1,
                        }}
                        component={TextFieldControlSimple}
                        validate={FormValidators.composeValidators(
                          FormValidators.required("Minimal altitude is required")
                        )}
                      />
                    </Section>
                    <Section title="Enter highest elevation" sx={{ paddingTop: { xs: 0, md: "24px" } }}>
                      <Field
                        formAPI={form}
                        name="elevationMax"
                        placeholder="Example 300 meters"
                        type="number"
                        sx={monoFieldSx}
                        input={{
                          onChange: (e: any) => {
                            form.mutators.setElevationMax(e.target.value);
                          },
                          name: "elevationMax",
                          value: values.elevationMax,
                          type: "number",
                          step: 1,
                        }}
                        component={TextFieldControlSimple}
                        validate={FormValidators.composeValidators(
                          FormValidators.required("Maximal altitude is required"),
                          FormValidators.gte(
                            values.elevationMin || values.elevationMin === 0 ? Number(values.elevationMin) : -1,
                            "Minimal altitude is required before filling maximal",
                            "Maximal altitude must be higher than minimal"
                          )
                        )}
                      />
                    </Section>
                  </SectionRow>
                  <Section title="Which of these best describes your land type?">
                    <SelectItemsInput name="biomes" onChange={form.change} values={values.biomes} />
                  </Section>

                  <SectionRow sx={{ gap: { xs: "18px", md: "24px" }, marginTop: 0 }}>
                    <Box sx={{ order: { xs: 1, md: 1 } }}>
                      <Section
                        invertTitle
                        title="List the land's particular plant and animal life."
                        desc="Flora and Fauna"
                      >
                        <MultiTextInput
                          placeholder={"Click Enter to separate items e.g. deer, peacock, teak, bamboo. "}
                          name="flora"
                          formAPI={form}
                          draft={true}
                          onChange={form.change}
                          initial={initialFlora}
                        />
                      </Section>
                      <Section
                        invertTitle
                        title="List any type of built structures on the land with number of accomodation"
                        desc="Dwellings"
                      >
                        <MultiTextInput
                          placeholder={
                            "Click Enter to separate items e.g. cabin-sleeps 4, campsite-sleeps 2, builder's quarters-sleeps 1, hut-sleeps 1"
                          }
                          name="dwellings"
                          formAPI={form}
                          initial={initialDwellings}
                          onChange={form.change}
                        />
                      </Section>
                      <Section
                        invertTitle
                        title="List any type of forest garden produce grown on the land."
                        desc="Produce"
                      >
                        <MultiTextInput
                          placeholder={"Click Enter to separate items e.g. eggplant, pepper"}
                          name="production"
                          formAPI={form}
                          initial={initialProduction}
                          onChange={form.change}
                        />
                      </Section>
                    </Box>

                    <Section
                      sx={{
                        order: { xs: 0, md: 2 },
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <InfoCard
                        sx={{ width: "auto", minHeight: "auto", maxHeight: "auto", m: { xs: "24px 0", md: 0 } }}
                        title="Need guidance?"
                        body="Identify the land’s flora, fauna, dwellings, and produce using short descriptive words only — avoid sentences or grouped phrases, as these will display as tags. Accurate entries influence the EcoScore, which determines EFT value by factoring in biodiversity, soil health, water quality, carbon storage, and ecological maturity. Clear, precise descriptors help ensure your land’s environmental value is recognized and rewarded."
                        link={AppRoutes.About.path + "?blog=blog-article-4"}
                      />
                    </Section>
                  </SectionRow>

                  <Section>
                    <ContentUploadNew
                      title="Upload an image to illustrate or signify the land"
                      draftImage={contentFile}
                      setFile={setContent}
                      setPreview={setPreview}
                      onlyGifs
                    />
                  </Section>
                  {/* <Section invertTitle title="Share a land survey or any other supporting documents." desc="Land survey">
                  <DocUpload file={surveyFile} setFile={setSurvey} title="Land survey plan .PDF" />
                </Section> */}
                </Step>

                {/*-Step 4-*/}
                <Step
                  btnSx={{ pb: sizing(6) }}
                  sx={{ paddingX: sizing(6), backgroundColor: "white", borderRadius: "20px" }}
                  step={3}
                  currentStep={activeStep}
                  nextStep={setStep}
                  form={form}
                  tokenId={tokenId}
                  newDraft={newDraft}
                  setTokenId={setTokenId}
                  axios={axios}
                  contentFile={contentFile}
                  surveyFile={surveyFile}
                  deedFile={deedFile}
                  sizeUnit={sizeUnit}
                  sizeValue={sizeValue}
                  setIsRequestLoading={setIsRequestLoading}
                  imagePreview={imagePreview}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  updateMaxContractPeriod={updateMaxContractPeriod}
                  lastSavedValuesRef={lastSavedValuesRef}
                  uploadedContentAliasRef={uploadedContentAliasRef}
                  hasFormDataChanged={hasFormDataChanged}
                >
                  {/* <Attention>Step 4 of 4</Attention> */}
                  <Section title="Tier 1">
                    <SectionRow>
                      <Section sx={{ pt: sizing(0) }} desc={`Starting bid amount for future auction`}>
                        <InputWithCurrency>
                          <Field
                            formAPI={form}
                            name="startingBid"
                            type="number"
                            sx={monoFieldSx}
                            component={TextFieldControlSimple}
                            validate={FormValidators.composeValidators(
                              FormValidators.required("Bid amount is required"),
                              FormValidators.ge(
                                1999.99,
                                "Bid amount must be greater than 2,000",
                                "Bid amount must be greater than 2,000"
                              )
                            )}
                          />
                          <span>{CURRENCY_NAME}</span>
                        </InputWithCurrency>
                      </Section>
                      <Section
                        sx={{ pt: sizing(0) }}
                        desc={`Period of contract between you and trust organization (${contractMin}-${contractMax} ${TERM_UNIT}) `}
                      >
                        <InputWithCurrency>
                          <Field
                            formAPI={form}
                            name="contract_period"
                            type="number"
                            sx={monoFieldSx}
                            component={TextFieldControlSimple}
                            max={contractMax}
                            placeholder={`e.g. 3`}
                            min={contractMin}
                            validate={FormValidators.composeValidators(
                              FormValidators.required("Contract period is required"),
                              FormValidators.inRange(
                                { from: contractMin, to: contractMax },
                                `Contract period should be at least ${contractMin} ${TERM_UNIT} but not more than ${contractMax} ${TERM_UNIT}`
                              )
                            )}
                          />
                          <span style={{ textTransform: "uppercase" }}>{TERM_UNIT}</span>
                        </InputWithCurrency>
                      </Section>
                    </SectionRow>
                    <Section desc={`List simple commitments (point form) you will complete during the contract period`}>
                      <Field
                        formAPI={form}
                        name="bfd_plan"
                        placeholder="e.g. Plant and maintain 1,000 native trees."
                        type="text"
                        multiline={true}
                        maxLength={255}
                        component={TextFieldControlSimple}
                        validate={FormValidators.composeValidators(
                          FormValidators.required(""),
                          FormValidators.maxValue(255, "Plan must be 255 characters or less")
                        )}
                      />
                      <CharCount value={values.bfd_plan} max={255} />
                    </Section>

                    <Section desc={`Enter land outputs in point form. Each entry will display as a tag.`}>
                      <MultiTextInput
                        name="basicPerks"
                        placeholder="Click Enter to separate items e.g. 300kg organic pepper, vegetables, timber"
                        onChange={form.change}
                        initial={initialPerks}
                      />
                    </Section>

                    <Section sx={{ pt: sizing(0) }} hidden={easementCount < 1}>
                      <Attention sx={{ border: "none" }} style={{ paddingBottom: "0" }}>
                        Tier 2
                      </Attention>
                      <Section sx={{ pt: { xs: "18px", md: "24px" } }} desc="Activation bid amount">
                        <InputWithCurrency>
                          <Field
                            formAPI={form}
                            name="easements[0].activationBid"
                            type="number"
                            sx={monoFieldSx}
                            component={TextFieldControlSimple}
                            validate={FormValidators.ge(
                              values.startingBid ? Number(values.startingBid) || -1 : -1,
                              "Start bid is required before filling easements bid",
                              "Easement activation bid must be higher than starting bid"
                            )}
                          />
                          <span>{CURRENCY_NAME}</span>
                        </InputWithCurrency>
                      </Section>

                      <Section desc="List simple commitments (point form) you will complete during the contract period">
                        <Field
                          formAPI={form}
                          name="easements[0].description"
                          placeholder="e.g. Plant and maintain 1,000 native trees."
                          type="text"
                          multiline={true}
                          maxLength={255}
                          component={TextFieldControlSimple}
                          validate={FormValidators.composeValidators(
                            FormValidators.requiredWhen(
                              values.easements ? !!values.easements[0]?.activationBid : false,
                              "activation bid"
                            ),
                            FormValidators.maxValue(255, "Description must be 255 characters or less")
                          )}
                        />
                        <CharCount value={values.easements?.[0]?.description} max={255} />
                      </Section>

                      <Section desc="Enter land outputs in point form. Each entry will display as a tag.">
                        <MultiTextInput
                          name="easements[0].perks"
                          placeholder="Click Enter to separate items eg. Annual profit share, Camping access, Recognition"
                          onChange={form.change}
                          initial={initialPerks1}
                        />
                      </Section>
                    </Section>

                    <Section sx={{ pt: sizing(0) }} hidden={easementCount < 2}>
                      <Attention sx={{ border: "none" }} style={{ paddingBottom: "0" }}>
                        Tier 3
                      </Attention>
                      <Section sx={{ pt: { xs: "18px", md: "24px" } }} desc="Activation bid amount">
                        <InputWithCurrency>
                          <Field
                            formAPI={form}
                            name="easements[1].activationBid"
                            type="number"
                            sx={monoFieldSx}
                            component={TextFieldControlSimple}
                            validateFields={["easements[1].description"]}
                            validate={FormValidators.composeValidators(
                              FormValidators.ge(
                                values.easements ? Number(values.easements[0]?.activationBid) || -1 : -1,
                                "Previous easement is required",
                                "Easement activation bid must be higher than previous easement bid"
                              ),
                              FormValidators.requiredWhen(
                                values.easements ? !!values.easements[1]?.description : false,
                                "description"
                              )
                            )}
                          />
                          <span>{CURRENCY_NAME}</span>
                        </InputWithCurrency>
                      </Section>

                      <Section desc="List simple commitments (point form) you will complete during the contract period">
                        <Field
                          formAPI={form}
                          name="easements[1].description"
                          placeholder="e.g. Plant and maintain 1,000 native trees."
                          type="text"
                          multiline={true}
                          maxLength={255}
                          component={TextFieldControlSimple}
                          validate={FormValidators.composeValidators(
                            FormValidators.requiredWhen(
                              values.easements ? !!values.easements[1]?.activationBid : false,
                              "activation bid"
                            ),
                            FormValidators.maxValue(255, "Description must be 255 characters or less")
                          )}
                        />
                        <CharCount value={values.easements?.[1]?.description} max={255} />
                      </Section>

                      <Section desc="Enter land outputs in point form. Each entry will display as a tag.">
                        <MultiTextInput
                          name="easements[1].perks"
                          placeholder="Click Enter to separate items eg. Annual profit share, Camping access, Recognition"
                          onChange={form.change}
                          initial={initialPerks2}
                        />
                      </Section>
                    </Section>

                    {/* <Section hidden={easementCount < 3}>
                    <Attention>Easement tier 3</Attention>
                    <Section>
                      <Field
                        label="Activation bid amount"
                        name="easements[2].activationBid"
                        type="number"
                        component={TextFieldControlSimple}
                        validate={FormValidators.ge(
                          values.easements ? Number(values.easements[1]?.activationBid) || -1 : -1,
                          "Previous easement is required",
                          "Easement activation bid must be higher than previous easement bid"
                        )}
                      />
                    </Section>
                    <Section>
                      <Field
                        label="Easement description"
                        name="easements[2].description"
                        type="text"
                        multiline={true}
                        component={TextFieldControlSimple}
                        validate={FormValidators.requiredWhen(
                          values.easements ? !!values.easements[2]?.activationBid : false,
                          "activation bid"
                        )}
                      />
                    </Section>

                    <Section desc="Easement type">
                      <MultiTextInput
                        name="easements[2].perks"
                        placeholder="Click Enter to separate items eg. Annual profit share, Camping access, Recognition"
                        onChange={form.change}
                      />
                    </Section>
                  </Section> */}

                    {easementCount < 2 && (
                      <ActionBtn
                        sx={{ p: 0, mt: sizing(6) }}
                        variant="text"
                        onClick={() => setEasementCount(easementCount + 1)}
                      >
                        + Add another tier.
                      </ActionBtn>
                    )}
                  </Section>
                </Step>

                <Step
                  sx={{
                    paddingX: sizing(6),
                    pb: sizing(6),
                    background: "var(--light-blue)",
                    color: "black",
                    borderRadius: "15px",
                  }}
                  form={form}
                  step={4}
                  currentStep={activeStep}
                  nextStep={setStep}
                  loading={isLoading}
                  invalid={invalid}
                  tokenId={tokenId}
                  newDraft={newDraft}
                  setTokenId={setTokenId}
                  axios={axios}
                  contentFile={contentFile}
                  surveyFile={surveyFile}
                  deedFile={deedFile}
                  sizeUnit={sizeUnit}
                  sizeValue={sizeValue}
                  setIsRequestLoading={setIsRequestLoading}
                  updateMaxContractPeriod={updateMaxContractPeriod}
                  lastSavedValuesRef={lastSavedValuesRef}
                  uploadedContentAliasRef={uploadedContentAliasRef}
                  hasFormDataChanged={hasFormDataChanged}
                  approveStatus={approveStatus}
                  isRejected={isRejected}
                >
                  <Grid
                    width="100%"
                    flexDirection="column"
                    display={"flex"}
                    sx={{
                      paddingX: { xs: sizing(4).xs, md: 0 },
                      paddingY: { xs: "16px", md: "20px" },
                      background: "transparent",
                      color: "black",
                      margin: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "stretch",
                        gap: { xs: "24px", md: "32px" },
                      }}
                    >
                      {/* Approve allowance */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "14px",
                          flex: "1 1 280px",
                          minWidth: 0,
                        }}
                      >
                        <Text
                          capital={false}
                          sx={{
                            display: "block",
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 700,
                            lineHeight: "25.6px",
                            color: "black",
                            textTransform: "none",
                          }}
                        >
                          Approve allowance. Your verification fee is calculated (
                          {verificationFeePerHectare
                            ? `${parseFloat(verificationFeePerHectare).toLocaleString(undefined, {
                                maximumFractionDigits: 4,
                              })} ${CURRENCY_NAME}`
                            : "…"}{" "}
                          per hectare).
                        </Text>

                        {verificationFee && (
                          <Box
                            sx={{
                              backgroundColor: "white",
                              borderRadius: "16px",
                              height: "44px",
                              minWidth: "180px",
                              width: "fit-content",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: "24px",
                            }}
                          >
                            <Text
                              capital={false}
                              sx={{
                                display: "block",
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: "16px",
                                fontWeight: 700,
                                lineHeight: "19.2px",
                                color: "black",
                                textAlign: "center",
                                textTransform: "none",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {parseFloat(verificationFee).toLocaleString(undefined, { maximumFractionDigits: 2 })}{" "}
                              {CURRENCY_NAME}
                            </Text>
                          </Box>
                        )}

                        {/* V10.1: the total is verification (deed check + survey + determination) plus the
                            country's flat attestation and judgment fees, pulled in one transfer. Only worth
                            itemising when the flat fees are non-zero for this country. */}
                        {verificationFeeParts &&
                          (parseFloat(verificationFeeParts.attestation) > 0 ||
                            parseFloat(verificationFeeParts.judgment) > 0) && (
                            <Text
                              capital={false}
                              sx={{
                                display: "block",
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "12px",
                                color: "#666",
                                textTransform: "none",
                              }}
                            >
                              Verification{" "}
                              {parseFloat(verificationFeeParts.scaled).toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}
                              {" + attestation "}
                              {parseFloat(verificationFeeParts.attestation).toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}
                              {" + judgment "}
                              {parseFloat(verificationFeeParts.judgment).toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}{" "}
                              {CURRENCY_NAME}
                            </Text>
                          )}

                        <ActionBtn
                          onClick={approveCollection}
                          disabled={approveStatus === "processing" || approveStatus === "success" || isRejected}
                          sx={{
                            width: "180px",
                            height: "44px",
                            borderRadius: "16px",
                            border: "1px solid black",
                            backgroundColor: approveStatus === "success" || isRejected ? "grey" : "black",
                            color: "white",
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            textTransform: "capitalize",
                            "&:hover": {
                              backgroundColor: "#333",
                              border: "none",
                              color: "white",
                            },
                          }}
                        >
                          {approveStatus === "processing"
                            ? "Waiting for approval..."
                            : approveStatus === "success" || isRejected
                            ? "Approved"
                            : "Approve"}
                        </ActionBtn>
                      </Box>

                      {/* Important final step info card */}
                      <Box
                        sx={{
                          flex: "1 1 320px",
                          minWidth: 0,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "16px",
                          backgroundColor: "#f2f2f2",
                          borderRadius: "16px",
                          p: { xs: "18px", md: "18px 32px 24px 24px" },
                        }}
                      >
                        <Box
                          sx={{
                            width: "52px",
                            height: "52px",
                            borderRadius: "26px",
                            backgroundColor: "#d1efff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <InfoIconOutlined sx={{ fontSize: "28px", color: "black" }} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
                          <Text
                            capital={false}
                            sx={{
                              display: "block",
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontWeight: 700,
                              fontSize: "16px",
                              lineHeight: "19.2px",
                              color: "black",
                              textTransform: "uppercase",
                            }}
                          >
                            Important final step!
                          </Text>
                          <Text
                            capital={false}
                            sx={{
                              display: "block",
                              fontFamily: "'Inter', sans-serif",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "18px",
                              color: "black",
                              textTransform: "none",
                            }}
                          >
                            Almost done! You've completed the application to list your land. Please review all details
                            carefully before proceeding.
                          </Text>
                          <Text
                            capital={false}
                            sx={{
                              display: "block",
                              fontFamily: "'Inter', sans-serif",
                              fontWeight: 700,
                              fontSize: "14px",
                              lineHeight: "18px",
                              color: "black",
                              textTransform: "none",
                            }}
                          >
                            {trusteeInCountry
                              ? "The verification usually takes about 3 weeks."
                              : "This may take up to 6 months to complete as there is currently no active Trustee in your country."}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                      {error}
                    </Alert>
                  )}
                </Step>

                {/* <BlogStoryBlock blog={blogs[4]} sx={{
                paddingX: sizing(6),
                backgroundColor: 'back.oliveGreen',
              }} /> */}
                <Trending
                  sx={{
                    position: "relative",
                    borderRadius: "20px 20px 20px 20px",
                    border: "none",
                  }}
                >
                  {!blogLoading && blogArticles["4"] && (
                    <>
                      <img
                        src={blogArticles["4"].image}
                        alt="Blog article"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                          filter: "brightness(1)",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)",
                          zIndex: 1,
                        }}
                      />
                      <BlogStoryBlock
                        blog={blogArticles["4"]}
                        sx={{
                          zIndex: 2,
                        }}
                        search={blogArticles["4"].Slug}
                        subtitle={blogArticles["4"].subtitle}
                        paragraph={blogArticles["4"].paragraph}
                      />
                    </>
                  )}
                </Trending>
                {/* <Grid width="100%" flexDirection="column" display="flex">
                <Attention sx={{ backgroundColor: "back.wheat", p: sizing(6), border: 'none', borderRadius: "20px" }}>
                  <Text large sx={{ color: "black", display: "inline" }}>
                    A meaningful investment giving you direct stakes in sustaining ecological assets;
                    <Link
                      style={{ color: "black", textDecoration: "underline", marginLeft: "6px" }}
                      to={AppRoutes.Explore.path}
                    >
                      start now.
                    </Link>
                  </Text>
                </Attention>
              </Grid> */}
              </Grid>
            </FormArea>
          )}
        />
      </Wrapper>
      <Modal
        open={open1}
        onClose={handleClose1}
        aria-labelledby="land-upload-modal-title"
        aria-describedby="land-upload-modal-description"
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0,0,0,0.5)" },
          },
        }}
        sx={{ width: "100%" }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: open1 ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.95)",
            transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
            width: { xs: "90%", md: "540px" },
            maxWidth: "540px",
            backgroundColor: "#ffffff",
            borderRadius: "24px",
            padding: { xs: "48px 24px 36px", md: "48px 40px 36px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            outline: "none",
            zIndex: 1,
          }}
        >
          {/* One-shot ripple burst, anchored to the icon center, blooming out
              behind the card on success. Mounted only after success so the
              keyframes run once on the false->true transition; guarded for
              reduced motion. */}
          {landUploadSuccess && !prefersReducedMotion && (
            <>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  width: "180px",
                  height: "180px",
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle, rgba(188,146,248,0.85) 0%, rgba(188,146,248,0.4) 40%, transparent 72%)",
                  filter: "blur(20px)",
                  pointerEvents: "none",
                  zIndex: -1,
                  animation: `${landUploadRippleOut} 1.8s cubic-bezier(0.16, 0.6, 0.35, 1) both`,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  width: "180px",
                  height: "180px",
                  borderRadius: "50%",
                  border: "2px solid rgba(188, 146, 248, 0.6)",
                  pointerEvents: "none",
                  zIndex: -1,
                  animation: `${landUploadRingOut} 1.8s cubic-bezier(0.16, 0.6, 0.35, 1) both`,
                }}
              />
            </>
          )}

          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "72px",
              height: "72px",
              background: "#ffffff",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: landUploadSuccess ? "#bc92f8" : "#d1efff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                transition: "background 0.4s ease",
              }}
            >
              {/* Spinning conic-gradient disc glow while uploading. */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background:
                    "conic-gradient(from 0deg, #2b3501, #6da832, #bc92f8, #9b6be8, #d1efff, #78cfff, #bc92f8, #6da832, #2b3501)",
                  mixBlendMode: "overlay",
                  opacity: landUploadSuccess ? 0 : 0.9,
                  animation: `${landUploadDiscSpin} 4.5s linear infinite`,
                  transition: "opacity 0.4s ease",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />
              <InfoIconOutlined
                sx={{
                  position: "absolute",
                  fontSize: "26px",
                  color: "#000000",
                  opacity: landUploadSuccess ? 0 : 1,
                  transform: landUploadSuccess ? "scale(0.6)" : "scale(1)",
                  transition: "opacity 0.25s ease, transform 0.25s ease",
                  zIndex: 1,
                }}
              />
              <CheckIcon
                sx={{
                  position: "absolute",
                  fontSize: "30px",
                  color: "#ffffff",
                  opacity: landUploadSuccess ? 1 : 0,
                  transform: landUploadSuccess ? "scale(1)" : "scale(0.6)",
                  transition: "opacity 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  zIndex: 1,
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              fontSize: "26px",
              fontWeight: 700,
              color: "#000000",
              marginTop: "12px",
              marginBottom: "16px",
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
            }}
          >
            {landUploadSuccess ? "Land Uploaded successfully" : "Land uploading..."}
          </Box>

          <Box
            sx={{
              fontSize: "15px",
              color: "#4a4a4a",
              lineHeight: 1.6,
              maxWidth: "440px",
              marginBottom: "28px",
              opacity: landUploadSuccess ? 1 : 0,
              transform: landUploadSuccess ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 0.4s ease, transform 0.4s ease",
            }}
          >
            Your land has been submitted for verification. You can now view it in your profile and track the entire
            verification process.
          </Box>

          <Button
            onClick={async () => {
              await refreshProfileData();
              navigator(`${AppRoutes.Profile.subRoutes.Collection.path}?activeTab=lands`);
            }}
            disableRipple
            sx={{
              background: "#000000",
              color: "#ffffff",
              border: "none",
              borderRadius: "50px",
              padding: "14px 44px",
              fontSize: "15px",
              fontWeight: 600,
              textTransform: "none",
              cursor: "pointer",
              opacity: landUploadSuccess ? 1 : 0,
              transform: landUploadSuccess ? "scale(1)" : "scale(0.9)",
              pointerEvents: landUploadSuccess ? "all" : "none",
              transition: "opacity 0.2s, transform 0.2s",
              "&:hover": {
                background: "#000000",
                color: "#ffffff",
                opacity: 0.85,
              },
            }}
          >
            View Land
          </Button>
        </Box>
      </Modal>
    </LoadingScreen>
  );
};

export const SectionRow = styled(Grid)`
  margin-top: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  position: relative;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 18px;
    margin-top: 18px;
  }
`;

const MapWithOverlay = styled("div")`
  position: relative;
  width: 100%;
  border-radius: 0 0 20px 20px;
  overflow: hidden;

  @media (max-width: 767px) {
    overflow: visible;
    border-radius: 0;
    & #map-box-container,
    & #map-box-container canvas {
      height: auto;
      border-radius: 20px 20px 0 0 !important;
    }
  }
`;

const LocationOverlayCard = styled("div", {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible?: boolean }>`
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 2;
  width: 360px;
  max-width: calc(100% - 48px);
  padding: 24px 24px;
  background: #000000;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  color: #ffffff;

  @media (min-width: 768px) {
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    pointer-events: ${({ visible }) => (visible ? "auto" : "none")};
    animation: ${({ visible }) => (visible ? `${overlaySlideIn} 600ms cubic-bezier(0.16, 1, 0.3, 1) both` : "none")};
    @media (prefers-reduced-motion: reduce) {
      animation: none;
      transition: opacity 200ms ease;
    }
  }

  input,
  textarea,
  .MuiInputBase-input,
  .MuiSelect-select {
    color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
  }
  label,
  .MuiInputLabel-root,
  .MuiFormLabel-root {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  input.Mui-disabled,
  .MuiInputBase-input.Mui-disabled {
    -webkit-text-fill-color: rgba(255, 255, 255, 0.5) !important;
  }
  label.Mui-disabled,
  .MuiInputLabel-root.Mui-disabled {
    color: rgba(255, 255, 255, 0.4) !important;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.3) !important;
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.6) !important;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #ffffff !important;
  }
  .MuiSelect-icon,
  .MuiSvgIcon-root {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  @media (max-width: 767px) {
    position: static;
    width: 100%;
    max-width: none;
    padding: 18px;
    border-radius: 0;
    box-shadow: none;
  }

  @media (max-width: 565px) {
    padding: 8px 12px 12px;
  }
`;

const FormArea = styled("form")`
  width: 100%;
`;

export const Preview = styled("img")`
  width: 100%;
  height: 100%;
`;

export const EasementInput = styled("div")`
  border-top: 4px dotted #ccc;
`;

export const SuccessBox = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  min-height: fit-content;
  user-select: none;

  img {
    max-width: 400px;
  }

  span {
    margin-bottom: 15px;
    font-size: 26px;

    &:nth-of-type(1) {
      font-size: 26px;
    }

    &:nth-of-type(3) {
      font-size: 16px;
      text-align: center;
    }
  }
`;

export const Section = ({ invertTitle, row, title, desc, children, hidden, sx }: any) => {
  return (
    <SectionWrapper hidden={hidden} sx={{ paddingTop: { xs: "18px", md: "24px" }, ...sx }}>
      {title && !invertTitle && <Text sx={{ mb: { xs: "18px", md: "24px" } }}>{title}</Text>}
      {desc && (
        <Text
          dangerouslySetInnerHTML={{ __html: desc }}
          sx={{
            color: "#818181",
            fontSize: "16px",
            fontWeight: "normal",
            mb: invertTitle ? { xs: "18px", md: "24px" } : { xs: "18px", md: "24px" },
          }}
        />
      )}
      {title && invertTitle && <Text sx={{ mb: { xs: "18px", md: "24px" } }}>{title}</Text>}
      {row ? <SectionRow>{children}</SectionRow> : children}
    </SectionWrapper>
  );
};

export const SectionWrapper = styled("div")`
  width: 100%;

  & > input[type="text"] {
    width: 100%;
  }

  &[hidden] {
    display: none;
  }
`;

export const Wrapper = styled("div")`
  max-width: 1560px;
  display: flex;

  & input[type="number"]::-webkit-inner-spin-button,
  & input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const ContactRow = styled("div")`
  display: flex;
  align-items: start;
`;

export const InputWithOption = styled("div")`
  display: flex;
  align-items: center;
  position: relative;
`;

export const InputOption = styled("div")`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 0;

  & > div > div {
    //padding: 12px 16px;
  }

  & fieldset {
    border: none;
  }
`;

export const InputResult = styled("div")`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  bottom: -20px;
  font-size: 12px;
  color: #818181;
  width: max-content;
  font-family: monospace;
  text-transform: uppercase;
  font-weight: 700;

  @media (max-width: 565px) {
    top: 50px;
    left: unset;
    right: 0;
  }
`;

export const InputWithCurrency = styled("div")`
  input {
    width: 100%;
    padding-right: 96px;
  }
  fieldset {
    top: 0;
  }

  display: flex;
  flex-direction: row;
  align-items: center;

  position: relative;

  span {
    display: block;
    position: absolute;
    right: 16px;
    font-family: IBM Mono, monospace;
    top: 20px;
    user-select: none;
    font-size: 16px;
  }
`;

const ActionButtonNew = styled(Button)({
  backgroundColor: "black",
  color: "white",
  border: "none",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

const ProcessingButton = styled(Button)({
  backgroundColor: "#333",
  color: "white",
  border: "none",
  "&:hover": {
    border: "none",
    backgroundColor: "#333",
  },
  "&.Mui-disabled": {
    backgroundColor: "#333",
    color: "white",
  },
});

const StepWrap = styled("div")`
  display: none;
  &.active {
    display: flex;
    flex-direction: column;
  }
  & > div > button {
    max-width: max-content;
  }
  & > div:last-of-type {
    color: red;
    grid-template-columns: 1fr 1fr !important;
  }
`;

type StepBlockProps = {
  children: React.ReactNode;
  step: number;
  currentStep: number;
  nextStep: (number: number) => void;
  form: FormApi<ScanValues>;
  validationItems?: any[];
  sx?: SxProps;
  btnSx?: SxProps;
  invalid?: boolean;
  loading?: boolean;
  tokenId?: string | null;
  newDraft?: boolean | undefined;
  axios?: any;
  setStep?: (number: number) => void;
  setTokenId?: (string: string) => void;
  contentFile?: File | undefined;
  surveyFile?: File | undefined;
  deedFile?: File | undefined;
  sizeUnit?: Unit;
  sizeValue?: number | string;
  setIsRequestLoading?: (loading: boolean) => void;
  imagePreview?: string;
  uploadedFiles?: any;
  setUploadedFiles?: any;
  updateMaxContractPeriod?: (landSizeInParcels: number) => Promise<void>;
  updateVerificationFee?: (landSizeInParcels: number) => Promise<void>;
  lastSavedValuesRef?: React.MutableRefObject<any>;
  uploadedContentAliasRef?: React.MutableRefObject<string | null>;
  hasFormDataChanged?: (currentValues: any, lastSavedValues: any) => boolean;
  approveStatus?: "idle" | "processing" | "success" | "error";
  isRejected?: boolean;
};

const Step = ({
  invalid,
  loading,
  btnSx,
  sx,
  children,
  step,
  currentStep,
  nextStep,
  form,
  validationItems,
  newDraft,
  setTokenId,
  axios,
  contentFile,
  surveyFile,
  deedFile,
  sizeUnit,
  sizeValue,
  setIsRequestLoading,
  imagePreview,
  uploadedFiles,
  setUploadedFiles,
  updateMaxContractPeriod,
  updateVerificationFee,
  lastSavedValuesRef,
  uploadedContentAliasRef,
  hasFormDataChanged,
  approveStatus,
  isRejected,
}: StepBlockProps) => {
  const [stepError, setStepError] = React.useState<string>("");
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isStepInvalid = (step: number) => {
    const formErrors = form.getState().errors || {};
    const errorKeys = Object.keys(formErrors);
    if (errorKeys) {
      switch (step) {
        case 0:
          {
            const deedCheckValue = form.getFieldState("deedCheck")?.value;
            const additionalContact0 = form.getFieldState("additional_contacts[0]" as any)?.value;
            const phoneCode = form.getFieldState("phoneCode")?.value;
            const additionalContact1 = form.getFieldState("additional_contacts[1]" as any)?.value;
            const additionalContact3 = form.getFieldState("additional_contacts[3]" as any)?.value;

            // All contact fields must be filled
            const anyContactEmpty =
              !additionalContact0 ||
              additionalContact0 === "" ||
              !phoneCode ||
              phoneCode === "" ||
              !additionalContact1 ||
              additionalContact1 === "" ||
              !additionalContact3 ||
              additionalContact3 === "";

            // Deed checkbox must be checked
            const deedCheckNotChecked = !deedCheckValue;

            // Invalid if any contact is empty OR deed checkbox is not checked
            if (anyContactEmpty || deedCheckNotChecked) {
              return true;
            }
          }
          return (
            !!(formErrors as any)["additional_contacts[0]"] ||
            !!(formErrors as any)["additional_contacts[1]"] ||
            !!(formErrors as any)["additional_contacts[2]"] ||
            !!(formErrors as any)["additional_contacts[3]"]
          );
        case -1:
          return false;
        case 1:
          return errorKeys.includes("location");
        case 2:
          if (validationItems && validationItems.length > 0) {
            if (!validationItems[0]) {
              return true;
            }
            if (!validationItems[1]) {
              return true;
            }
          }
          return (
            errorKeys.includes("description") ||
            errorKeys.includes("size") ||
            errorKeys.includes("elevationMin") ||
            errorKeys.includes("elevationMax")
          );
        default:
          return (
            errorKeys.includes("startingBid") || errorKeys.includes("bfd_plan") || errorKeys.includes("contract_period")
          );
      }
    } else {
      return false;
    }
  };
  const handleNextStep = async (step: number, form: FormApi<ScanValues>, newDraft: boolean | undefined) => {
    console.log("📋 [STEP TRANSITION] handleNextStep called:", {
      currentStep: step,
      targetStep: step + 1,
      newDraft,
    });

    // Update max contract period and verification fee when moving from step 2 (land description) to step 3
    if (step === 2) {
      const currentParcels = form.getState().values.parcels;
      console.log("🔄 [STEP TRANSITION] Moving from step 2 to 3 - checking blockchain update:", {
        currentParcels,
        hasUpdateFunction: !!updateMaxContractPeriod,
        shouldCallBlockchain: currentParcels > 0 && !!updateMaxContractPeriod,
      });

      if (currentParcels > 0 && updateMaxContractPeriod) {
        console.log("🚀 [STEP TRANSITION] Triggering blockchain call for max contract period update");
        await updateMaxContractPeriod(currentParcels);
        console.log("✅ [STEP TRANSITION] Blockchain call completed");
      } else {
        console.log("⏭️ [STEP TRANSITION] Skipping blockchain call - conditions not met");
      }

      if (currentParcels > 0 && updateVerificationFee) {
        console.log("🚀 [STEP TRANSITION] Fetching verification fee for parcels:", currentParcels);
        await updateVerificationFee(currentParcels);
        console.log("✅ [STEP TRANSITION] Verification fee fetched");
      }
    } else {
      console.log("⏭️ [STEP TRANSITION] Not step 2->3 transition, skipping blockchain call");
    }

    // The wizard is linear, so validating `step` means every step up to it is done. Sent
    // with each draft save for the upload-progress notification; the API merges it into
    // what it has, so a re-save from an earlier step never lowers the progress. Capped at
    // the four wizard steps (0–3): the review step (4) has no Next.
    const completedSteps = Array.from({ length: Math.min(step + 1, 4) }, (_, index) => index);

    if (step != -1 && newDraft) {
      const formValues = form.getState().values;

      // Check if form data has changed before making API call
      // Also check if there's a new content file to upload (since contentFile is React state, not a form field)
      const isNewContentFile = contentFile && (contentFile as any) instanceof Blob && !(contentFile as any).isUrlOnly;
      const hasChanges =
        (hasFormDataChanged && hasFormDataChanged(formValues, lastSavedValuesRef?.current)) || isNewContentFile;
      console.log("[SAVE CHECK] newDraft path:", {
        hasFormDataChanged: hasFormDataChanged?.(formValues, lastSavedValuesRef?.current),
        isNewContentFile,
        hasChanges,
      });
      if (!hasChanges) {
        console.log("[SAVE OPTIMIZATION] No changes detected, skipping API call for newDraft");
        nextStep(step + 1);
        return;
      }

      setIsRequestLoading?.(true);
      try {
        // Make sure phone number has country code
        if (
          formValues.additional_contacts &&
          formValues.additional_contacts[1] &&
          !formValues.additional_contacts[1].includes("-") &&
          formValues.phoneCode
        ) {
          const phoneNumber = formValues.phoneCode + "-" + formValues.additional_contacts[1];
          formValues.additional_contacts[1] = phoneNumber;
        }

        const payload = new FormData();

        if (formValues.tokenId) {
          payload.append("tokenId", formValues.tokenId);
        }
        console.log("Form Values:", formValues);
        console.log("Image Preview:", imagePreview);
        // Check if contentFile is a new File upload (not a URL-only reference from a loaded draft)
        const isNewContentFile = contentFile && (contentFile as any) instanceof Blob && !(contentFile as any).isUrlOnly;
        console.log("Content file check:", {
          contentFile,
          isNewContentFile,
          isUrlOnly: (contentFile as any)?.isUrlOnly,
        });
        if (isNewContentFile) {
          if (uploadedContentAliasRef?.current) {
            // File was already uploaded to S3 on a previous step — reuse the alias.
            payload.append("contentAlias", uploadedContentAliasRef.current);
          } else {
            try {
              const contentUploadResponse = await axios.post(ApiRoutes.token.getUploadLink, {
                contentType: (contentFile as any).type || "image/jpeg",
              });

              const uploadUrl = contentUploadResponse.data.url;
              const contentAlias = contentUploadResponse.data.contentAlias;
              const uploadResponse = await fetch(uploadUrl, {
                method: "PUT",
                body: contentFile,
                headers: {
                  "Content-Type": contentFile.type,
                },
              });

              if (uploadResponse.status !== 201 && uploadResponse.status !== 200) {
                throw new Error("Failed to upload content file!");
              }
              if (uploadedContentAliasRef) {
                uploadedContentAliasRef.current = contentAlias;
              }
              payload.append("contentAlias", contentAlias);
            } catch (uploadError) {
              console.error("Failed to upload content file:", uploadError);
            }
          }
        }

        // Only upload survey file if it's new or changed
        const shouldUploadSurvey =
          surveyFile &&
          (!uploadedFiles.surveyPlan || // No existing survey file uploaded
            uploadedFiles.surveyPlan.name !== surveyFile.name || // Different file name
            uploadedFiles.surveyPlan.size !== surveyFile.size); // Different file size
        if (shouldUploadSurvey) {
          payload.append("surveyPlan", surveyFile);
        }

        // Only upload deed file if it's new or changed and deedCheck is false
        const shouldUploadDeed =
          deedFile &&
          !formValues.deedCheck &&
          (!uploadedFiles.deed || // No existing deed file uploaded
            uploadedFiles.deed.name !== deedFile.name || // Different file name
            uploadedFiles.deed.size !== deedFile.size); // Different file size
        if (shouldUploadDeed) {
          payload.append("deed", deedFile);
        }
        const tokenMeta = {
          ...formValues,
          biomes: formValues.biomes && typeof formValues.biomes === "string" ? formValues.biomes.split(",") : [],
          flora:
            formValues?.flora && Array.isArray(formValues.flora) ? formValues.flora.map((item: any) => item.label) : [],
          fauna: [],
          size: `${sizeValue},${sizeUnit}`,
          zones: [],
          dwellings:
            formValues.dwellings && Array.isArray(formValues.dwellings)
              ? formValues.dwellings.map((item: any) => item.label)
              : [],
          production:
            formValues.production && Array.isArray(formValues.production)
              ? formValues.production.map((item: any) => item.label)
              : [],
          contractPeriod: Number(formValues.contract_period) || 0,
          startingBid: formValues.startingBid ? toWei(formValues.startingBid, "ether") : "0",
          basicPerks:
            formValues.basicPerks && Array.isArray(formValues.basicPerks)
              ? formValues.basicPerks.map((item: any) => item.label)
              : [],
        };

        payload.append("tokenMeta", JSON.stringify(tokenMeta));
        payload.append("completedSteps", JSON.stringify(completedSteps));
        if (formValues.easements) {
          const easements: any = formValues.easements
            .filter((item: any) => !!item.activationBid)
            .map((item: any, index: number) => {
              return {
                level: index + 1,
                description: item.description,
                activationBid: toWei(item.activationBid, "ether"),
                perks: item.perks.map((perk: any) => perk.label),
              };
            });

          payload.append("easements", JSON.stringify(easements));
        }
        const result = await axios.post(ApiRoutes.token.postDraft, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Update uploaded files state after successful upload
        if (shouldUploadSurvey && surveyFile) {
          setUploadedFiles((prev: any) => ({
            ...prev,
            surveyPlan: { name: surveyFile.name, size: surveyFile.size },
          }));
        }
        if (shouldUploadDeed && deedFile) {
          setUploadedFiles((prev: any) => ({
            ...prev,
            deed: { name: deedFile.name, size: deedFile.size },
          }));
        }

        if (result.data && setTokenId && payload.get("tokenId") === null) {
          payload.append("tokenId", result.data.id);
          setTokenId(result.data.id);
          formValues.tokenId = result.data.id;
        }

        // Update lastSavedValuesRef after successful save
        if (lastSavedValuesRef) {
          lastSavedValuesRef.current = JSON.parse(JSON.stringify(formValues));
          console.log("[SAVE OPTIMIZATION] Updated lastSavedValuesRef after successful save");
        }
      } catch (error: any) {
        console.error("Failed to save draft token:", error);
        const msg = error?.response?.data?.message || error?.message || "Failed to save draft. Please try again.";
        setStepError(msg);
        setIsRequestLoading?.(false);
        return;
      } finally {
        setIsRequestLoading?.(false);
      }
    }

    if (step != -1 && !newDraft) {
      const formValues = form.getState().values;

      // Check if form data has changed before making API call
      // Also check if there's a new content file to upload (since contentFile is React state, not a form field)
      const isNewContentFile = contentFile && (contentFile as any) instanceof Blob && !(contentFile as any).isUrlOnly;
      const hasChanges =
        (hasFormDataChanged && hasFormDataChanged(formValues, lastSavedValuesRef?.current)) || isNewContentFile;
      console.log("[SAVE CHECK] existing draft path:", {
        hasFormDataChanged: hasFormDataChanged?.(formValues, lastSavedValuesRef?.current),
        isNewContentFile,
        hasChanges,
      });
      if (!hasChanges) {
        console.log("[SAVE OPTIMIZATION] No changes detected, skipping API call for existing draft");
        nextStep(step + 1);
        return;
      }

      setIsRequestLoading?.(true);
      try {
        // Make sure phone number has country code
        if (
          formValues.additional_contacts &&
          formValues.additional_contacts[1] &&
          !formValues.additional_contacts[1].includes("-") &&
          formValues.phoneCode
        ) {
          const phoneNumber = formValues.phoneCode + "-" + formValues.additional_contacts[1];
          formValues.additional_contacts[1] = phoneNumber;
        }

        if (formValues.tokenId) {
          if (setTokenId) {
            setTokenId(formValues.tokenId);
          }
          const payload = new FormData();
          console.log("Form Values:", formValues);
          console.log("Image Preview:", imagePreview);
          // Check if contentFile is a new File upload (not a URL-only reference from a loaded draft)
          const isNewContentFile =
            contentFile && (contentFile as any) instanceof Blob && !(contentFile as any).isUrlOnly;
          console.log("Content file check:", {
            contentFile,
            isNewContentFile,
            isUrlOnly: (contentFile as any)?.isUrlOnly,
          });
          if (isNewContentFile) {
            if (uploadedContentAliasRef?.current) {
              // File was already uploaded to S3 on a previous step — reuse the alias.
              payload.append("contentAlias", uploadedContentAliasRef.current);
            } else {
              try {
                const contentUploadResponse = await axios.post(ApiRoutes.token.getUploadLink, {
                  contentType: (contentFile as any).type || "image/jpeg",
                });

                const uploadUrl = contentUploadResponse.data.url;
                const contentAlias = contentUploadResponse.data.contentAlias;
                const uploadResponse = await fetch(uploadUrl, {
                  method: "PUT",
                  body: contentFile,
                  headers: {
                    "Content-Type": contentFile.type,
                  },
                });

                if (uploadResponse.status !== 201 && uploadResponse.status !== 200) {
                  throw new Error("Failed to upload content file!");
                }
                if (uploadedContentAliasRef) {
                  uploadedContentAliasRef.current = contentAlias;
                }
                payload.append("contentAlias", contentAlias);
              } catch (uploadError) {
                console.error("Failed to upload content file:", uploadError);
              }
            }
          }

          // Only upload survey file if it's new or changed
          const shouldUploadSurvey =
            surveyFile &&
            (!uploadedFiles.surveyPlan || // No existing survey file uploaded
              uploadedFiles.surveyPlan.name !== surveyFile.name || // Different file name
              uploadedFiles.surveyPlan.size !== surveyFile.size); // Different file size
          if (shouldUploadSurvey) {
            payload.append("surveyPlan", surveyFile);
          }

          // Only upload deed file if it's new or changed and deedCheck is false
          const shouldUploadDeed =
            deedFile &&
            !formValues.deedCheck &&
            (!uploadedFiles.deed || // No existing deed file uploaded
              uploadedFiles.deed.name !== deedFile.name || // Different file name
              uploadedFiles.deed.size !== deedFile.size); // Different file size
          if (shouldUploadDeed) {
            payload.append("deed", deedFile);
          }
          // Get current size values from form state instead of closure
          const currentSizeValue = sizeValue;
          const currentSizeUnit = sizeUnit;

          const tokenMeta = {
            ...formValues,
            biomes: formValues.biomes && typeof formValues.biomes === "string" ? formValues.biomes.split(",") : [],
            flora:
              formValues.flora && Array.isArray(formValues.flora)
                ? formValues.flora.map((item: any) => item.label)
                : [],
            fauna: [],
            size: `${currentSizeValue},${currentSizeUnit}`,
            zones: [],
            dwellings:
              formValues.dwellings && Array.isArray(formValues.dwellings)
                ? formValues.dwellings.map((item: any) => item.label)
                : [],
            production:
              formValues.production && Array.isArray(formValues.production)
                ? formValues.production.map((item: any) => item.label)
                : [],
            contractPeriod: Number(formValues.contract_period) || 0,
            startingBid: formValues.startingBid ? toWei(formValues.startingBid, "ether") : "0",
            basicPerks:
              formValues.basicPerks && Array.isArray(formValues.basicPerks)
                ? formValues.basicPerks.map((item: any) => item.label)
                : [],
          };
          payload.append("tokenMeta", JSON.stringify(tokenMeta));
          payload.append("tokenId", formValues.tokenId);
          payload.append("completedSteps", JSON.stringify(completedSteps));
          if (formValues.easements) {
            const easements: any = formValues.easements
              .filter((item: any) => !!item.activationBid)
              .map((item: any, index: number) => {
                return {
                  level: index + 1,
                  description: item.description,
                  activationBid: toWei(item.activationBid, "ether"),
                  perks: item.perks.map((perk: any) => perk.label),
                };
              });

            payload.append("easements", JSON.stringify(easements));
          }
          const result = await axios.post(ApiRoutes.token.postDraft, payload, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          // Update uploaded files state after successful upload
          if (shouldUploadSurvey && surveyFile) {
            setUploadedFiles((prev: any) => ({
              ...prev,
              surveyPlan: { name: surveyFile.name, size: surveyFile.size },
            }));
          }
          if (shouldUploadDeed && deedFile) {
            setUploadedFiles((prev: any) => ({
              ...prev,
              deed: { name: deedFile.name, size: deedFile.size },
            }));
          }

          if (result.data && setTokenId) {
            setTokenId(result.data.id);
          }

          // Update lastSavedValuesRef after successful save
          if (lastSavedValuesRef) {
            lastSavedValuesRef.current = JSON.parse(JSON.stringify(formValues));
            console.log("[SAVE OPTIMIZATION] Updated lastSavedValuesRef after successful save (existing draft)");
          }
        }
      } catch (error: any) {
        console.error("Failed to save draft token:", error);
        const msg = error?.response?.data?.message || error?.message || "Failed to save draft. Please try again.";
        setStepError(msg);
        setIsRequestLoading?.(false);
        return;
      } finally {
        setIsRequestLoading?.(false);
      }
    }
    setStepError("");
    nextStep(step + 1);
  };

  return (
    <StepWrap className={currentStep === step ? "active" : ""} sx={sx}>
      {children}
      <Box
        sx={{
          marginTop: sizing(6),
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          ...btnSx,
        }}
      >
        {stepError && (
          <Alert severity="error" onClose={() => setStepError("")}>
            {stepError}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <ActionButtonNew
            variant="contained"
            sx={{ flexShrink: 0, visibility: currentStep !== -1 ? "visible" : "hidden" }}
            onClick={() => {
              setStepError("");
              nextStep(step - 1);
            }}
            disabled={loading}
            disableElevation
            disableRipple
            disableFocusRipple
            disableTouchRipple
          >
            Back
          </ActionButtonNew>
          <Box sx={{ flex: 1 }} />
          <ActionButtonNew
            variant="contained"
            sx={{ display: step === 4 ? "none" : "flex", flexShrink: 0 }}
            disabled={isStepInvalid(step)}
            disableElevation
            disableRipple
            disableFocusRipple
            disableTouchRipple
            onClick={() => handleNextStep(step, form, newDraft)}
          >
            {step === -1 ? "Get Started" : "Next"}
          </ActionButtonNew>
          {loading ? (
            <ProcessingButton
              variant="contained"
              sx={{ display: step === 4 ? "flex" : "none", flexShrink: 0 }}
              disabled
              type="submit"
              disableElevation
              disableRipple
              disableFocusRipple
              disableTouchRipple
            >
              <CircularProgress size={20} sx={{ color: "white" }} />
            </ProcessingButton>
          ) : (
            <ActionButtonNew
              variant="contained"
              sx={{ display: step === 4 ? "flex" : "none", flexShrink: 0 }}
              disabled={invalid || (step === 4 && approveStatus !== "success" && !isRejected)}
              type="submit"
              disableElevation
              disableRipple
              disableFocusRipple
              disableTouchRipple
            >
              Upload
            </ActionButtonNew>
          )}
        </Box>
      </Box>
    </StepWrap>
  );
};

export default Scan;
